import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Download, FileSpreadsheet, FileText, ArrowRight, Calculator, Ruler, Package, Hammer, ChevronDown, ChevronUp, Scissors, IndianRupee, Camera, Cpu, Recycle, Tag, BarChart3, Library, Sparkles, Activity, X, FileSignature, Mail, Copy, GitCompareArrows, Info, Eye } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { API } from '@/App';
import * as XLSX from 'xlsx';
import CuttingLayoutEngine from '@/components/estimator/CuttingLayoutEngine';
import CostSheetEngine from '@/components/estimator/CostSheetEngine';
import AdvancedCutList from '@/components/estimator/AdvancedCutList';
import ErrorPrevention from '@/components/estimator/ErrorPrevention';
import RenderEngine from '@/components/estimator/RenderEngine';
import CNCExportEngine from '@/components/estimator/CNCExportEngine';
import RemnantManager from '@/components/estimator/RemnantManager';
import LabelQRSystem from '@/components/estimator/LabelQRSystem';
import AnalyticsDashboard from '@/components/estimator/AnalyticsDashboard';
import TemplateLibrary from '@/components/estimator/TemplateLibrary';
import SmartSuggestions from '@/components/estimator/SmartSuggestions';
import ProductionWorkflow from '@/components/estimator/ProductionWorkflow';
import KitchenBuilder from '@/components/estimator/KitchenBuilder';
import { calculateKitchen } from '@/components/estimator/kitchenCalc';
import { HARDWARE_BRANDS, STORAGE_ELECTRICAL_ITEMS, STORAGE_EXTRA_HARDWARE, STORAGE_HARDWARE_DEFAULTS } from '@/components/estimator/storageConfig';
import { BrandRateRow, QtyRateGrid } from '@/components/estimator/StorageHardwarePanels';
import { BrandPortfolioSwitcher } from '@/components/estimator/BrandPortfolioSwitcher';
import { BRAND_PORTFOLIO_PRESETS, resolvePreset } from '@/components/estimator/storageConfig';
import {
  KITCHEN_LAYOUTS,
  CARCASS_BY_ID,
  SHUTTER_BY_ID,
  BASE_UNITS,
  WALL_UNITS,
  TALL_UNITS,
  COUNTERTOP_BY_ID,
  EDGE_BY_ID,
  BACKSPLASH_BY_ID,
  APPLIANCE_BY_ID,
  SINK_BY_ID,
  FAUCET_BY_ID,
  ELECTRICAL_ITEMS,
  DRAWER_BY_ID,
  TROLLEY_BY_ID,
} from '@/components/estimator/kitchenConfig';

const KITCHEN_LAYOUTS_LABELS = Object.fromEntries(KITCHEN_LAYOUTS.map(l => [l.id, l.label]));

const DEFAULT_KITCHEN_STATE = {
  layout: 'straight',
  totalRunningFt: 10,
  ceilingHeight: 10,
  wall1: 10, wall2: 0,
  defaults: { carcass: 'bwr', shutter: 'laminate', carcassIncluded: true, shutterIncluded: true, carcassRateOverride: null, shutterRateOverride: null },
  modules: [],
  countertop: { material: 'granite', runningFt: 10, depthFt: 2, thicknessMm: 18, edge: 'plain', sinkCutout: true, hobCutout: true },
  backsplash: { material: 'tile', runningFt: 10, heightFt: 2 },
  appliances: {},
  appliancesMode: 'allowance',
  sink: { type: 'single_ss', faucet: 'basic', roCutout: true },
  plumbing: { scope: 'standard', billingMode: 'lump', addons: {}, includeMaterial: false, customRate: 0, customLabel: '' },
  plumbingRates: {}, // admin override per scope
  plumbingAddonRates: {},
  plumbingMaterialFlat: 3500,
  drawerRates: {},  // admin override for drawer systems
  trolleyRates: {},
  electrical: { socket: 4, under_cabinet_led: 8, strip_light: 0, driver: 1, sensor: 0, appliance_point: 3 },
};

export default function WardrobeCalculator({ initialProductType = null }) {
  const navigate = useNavigate();
  const { estimateId } = useParams();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('design');
  const [estimateName, setEstimateName] = useState(initialProductType === 'kitchen' ? 'New Kitchen Estimate' : 'New Wardrobe Estimate');
  const [linkedProject, setLinkedProject] = useState('');
  const [linkedClient, setLinkedClient] = useState('');
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [existingEstimate, setExistingEstimate] = useState(null);
  const [showRates, setShowRates] = useState(false);
  const [notes, setNotes] = useState('');

  // Dimensions
  const [width, setWidth] = useState(4);
  const [height, setHeight] = useState(8);
  const [depth, setDepth] = useState(2);
  const [wardrobeType, setWardrobeType] = useState('hinged');
  // Explicit product type (set by templates; overrides name-based detection)
  const [explicitProductType, setExplicitProductType] = useState(initialProductType || null);
  const [mouldingProfile, setMouldingProfile] = useState(1.5); // inches (1, 1.5, 2)
  const [mouldingRate, setMouldingRate] = useState(80); // Rs per running foot (default for 1.5")
  // Kitchen-specific state (used only when productType === 'kitchen')
  const [kitchen, setKitchen] = useState(DEFAULT_KITCHEN_STATE);
  // Commercial (applies to both kitchen and wardrobe estimates)
  const [overhead, setOverhead] = useState({ freight: 0, labour_handling: 0, consultancy: 0 });
  const [discount, setDiscount] = useState({ type: 'fixed', value: 0 });
  const [gst, setGst] = useState({ type: 'percent', value: 18 });
  const [termsText, setTermsText] = useState('');
  const [generatingTerms, setGeneratingTerms] = useState(false);
  // Handle profile (used in both wardrobes and kitchens)
  const [handleProfile, setHandleProfile] = useState('pull_handle'); // pull_handle | g_profile | j_profile | gola
  const [handleRate, setHandleRate] = useState(250);
  // Countertop / Backsplash installation labour rate (kitchen)
  const [countertopLaborRate, setCountertopLaborRate] = useState(120); // Rs/sqft
  const [backsplashLaborRate, setBacksplashLaborRate] = useState(80);  // Rs/sqft
  // iter-290: Custom Cut List ordering — array of part `code`s. Persisted at
  // the page level so reorders applied in the Advanced Cut List tab also flow
  // through to the Cutting Layout and CNC Export tabs (and their PDFs).
  const [cutListOrder, setCutListOrder] = useState([]);

  // Configuration
  const [shelfCount, setShelfCount] = useState(3);
  const [drawerCount, setDrawerCount] = useState(2);
  const [drawerWidthType, setDrawerWidthType] = useState('half');
  const [drawerHeight, setDrawerHeight] = useState(8);
  const [includeGlassProfile, setIncludeGlassProfile] = useState(false);

  // Material Rates (INR)
  const [ply18mm, setPly18mm] = useState(58);
  const [ply12mm, setPly12mm] = useState(50);
  const [ply8mm, setPly8mm] = useState(30);
  const [hdhmr16mm, setHdhmr16mm] = useState(85);
  const [internalLaminateRate, setInternalLaminateRate] = useState(500);
  const [externalLaminateRate, setExternalLaminateRate] = useState(1800);
  const [laborCost, setLaborCost] = useState(450);
  const [telescopicDrawerCost, setTelescopicDrawerCost] = useState(450);
  const [edgeBandingRate, setEdgeBandingRate] = useState(7);
  const [glassProfileCost, setGlassProfileCost] = useState(1200);
  const [transportCost, setTransportCost] = useState(0);
  const [designChargePercent, setDesignChargePercent] = useState(5);
  const [profitMargin, setProfitMargin] = useState(10);
  // Hardware rates (smart shutter/hinge system)
  const [hingeRate, setHingeRate] = useState(120);          // Rs per hinge (Blum/Hettich standard)
  const [slidingChannelRate, setSlidingChannelRate] = useState(2500); // Rs per 1m track set
  const [straightenerRate, setStraightenerRate] = useState(350); // Rs per door straightener
  // iter-296: Per-estimate hardware brand selections + per-line rate overrides.
  // Storage Estimator now mirrors Kitchen — pick a brand for each hardware
  // category, and the chosen brand's rate auto-fills the inputs above.
  const [hingeBrand, setHingeBrand] = useState('hettich');
  const [hingeBrandLabel, setHingeBrandLabel] = useState('');     // for custom
  const [straightenerBrand, setStraightenerBrand] = useState('hettich');
  const [straightenerBrandLabel, setStraightenerBrandLabel] = useState('');
  const [telescopicBrand, setTelescopicBrand] = useState('standard_450');
  const [telescopicBrandLabel, setTelescopicBrandLabel] = useState('');
  const [slidingBrand, setSlidingBrand] = useState('generic');
  const [slidingBrandLabel, setSlidingBrandLabel] = useState('');
  const [handleBrand, setHandleBrand] = useState('cabinet_handle');
  const [handleBrandLabel, setHandleBrandLabel] = useState('');
  // iter-296: Electrical line items (qty editable; rate overrides default).
  const [electricalLines, setElectricalLines] = useState(
    STORAGE_ELECTRICAL_ITEMS.map(it => ({ id: it.id, label: it.label, unit: it.unit, qty: 0, rate: it.rate }))
  );
  const [electricianLaborPct, setElectricianLaborPct] = useState(STORAGE_HARDWARE_DEFAULTS.electricianLaborPct);
  // iter-296: Additional hardware extras (gas lift, locks, etc).
  const [extraHardware, setExtraHardware] = useState(
    STORAGE_EXTRA_HARDWARE.map(it => ({ id: it.id, label: it.label, unit: it.unit, qty: 0, rate: it.rate }))
  );
  // AI Render (lifted from RenderEngine so Client Quote PDF can include thumbnail)
  const [renderedImage, setRenderedImage] = useState(null);
  const [generatingQuote, setGeneratingQuote] = useState(false);
  // === Cost Sheet markup state (lifted from CostSheetEngine so PDF/email can use Final Client Price) ===
  const [adminMarkup, setAdminMarkup] = useState(20);
  const [clientMarkup, setClientMarkup] = useState(20);
  const [minMarginAlert, setMinMarginAlert] = useState(15);
  const [extraOverheadPct, setExtraOverheadPct] = useState(0);
  // Applied Smart Upgrade Suggestions (mutates state below in real-time)
  const [appliedSuggestions, setAppliedSuggestions] = useState([]);
  const applySuggestion = (id) => {
    if (appliedSuggestions.includes(id)) return;
    if (id === 'glass_profile') setIncludeGlassProfile(true);
    else if (id === 'soft_close') setHingeRate(180); // soft-close hinge rate
    else if (id === 'premium_laminate') {
      // Upgrade shutter default to acrylic + re-apply to all existing kitchen modules
      if (isKitchen) {
        const nextModules = (kitchen.modules || []).map(m => ({ ...m, shutter: 'acrylic' }));
        setKitchen({ ...kitchen, defaults: { ...kitchen.defaults, shutter: 'acrylic' }, modules: nextModules });
      } else {
        // Wardrobe: bump external laminate rate
        setExternalLaminateRate(Math.max(externalLaminateRate, 2500));
      }
    } else if (id === 'led_strip') {
      if (isKitchen) {
        setKitchen({ ...kitchen, electrical: { ...(kitchen.electrical || {}), under_cabinet_led: Math.max(+(kitchen.electrical?.under_cabinet_led || 0), Math.ceil(kitchen.totalRunningFt || 8)) } });
      }
    }
    setAppliedSuggestions(prev => [...prev, id]);
    toast.success(`Applied: ${id.replace(/_/g, ' ')}`);
  };
  // Email Quote modal
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailCC, setEmailCC] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Load projects/clients for linking
  useEffect(() => {
    const load = async () => {
      try {
        const [projRes, clientRes] = await Promise.all([
          axios.get(`${API}/projects`, { headers }),
          axios.get(`${API}/users?role=client`, { headers }).catch(() => ({ data: [] })),
        ]);
        setProjects(projRes.data || []);
        setClients(clientRes.data || []);
      } catch {}
    };
    load();
  }, []);

  // iter-386u-ah — Beta Estimator quota consume on mount (owner bypassed server-side).
  // Guard against React StrictMode double-invoke via useRef so non-owner quota
  // is not double-charged in dev builds.
  const betaConsumedRef = useRef(false);
  useEffect(() => {
    if (betaConsumedRef.current) return;
    betaConsumedRef.current = true;
    const tool = isKitchen ? 'kitchen' : 'storage';
    axios.post(`${API}/beta/estimator/consume?tool=${tool}`, {}, { headers }).catch(() => {});
  }, []);

  // Load existing estimate if editing
  useEffect(() => {
    if (!estimateId) return;
    const loadEstimate = async () => {
      try {
        const res = await axios.get(`${API}/estimates/${estimateId}`, { headers });
        const e = res.data;
        setExistingEstimate(e);
        setEstimateName(e.name || '');
        setLinkedProject(e.project_id || '');
        setLinkedClient(e.client_id || '');
        setNotes(e.notes || '');
        if (e.dimensions) { setWidth(e.dimensions.width || 4); setHeight(e.dimensions.height || 8); setDepth(e.dimensions.depth || 2); }
        if (e.config) {
          setWardrobeType(e.config.wardrobeType || 'hinged');
          setShelfCount(e.config.shelfCount ?? 3);
          setDrawerCount(e.config.drawerCount ?? 2);
          setDrawerWidthType(e.config.drawerWidthType || 'half');
          setDrawerHeight(e.config.drawerHeight ?? 8);
          setIncludeGlassProfile(e.config.includeGlassProfile || false);
          if (e.config.productType) setExplicitProductType(e.config.productType);
          if (e.config.kitchen) setKitchen({ ...DEFAULT_KITCHEN_STATE, ...e.config.kitchen });
          if (e.config.handleProfile) setHandleProfile(e.config.handleProfile);
          if (e.config.handleRate) setHandleRate(e.config.handleRate);
          if (e.config.countertopLaborRate) setCountertopLaborRate(e.config.countertopLaborRate);
          if (e.config.backsplashLaborRate) setBacksplashLaborRate(e.config.backsplashLaborRate);
          if (e.config.overhead) setOverhead(e.config.overhead);
          if (e.config.discount) setDiscount(e.config.discount);
          if (e.config.gst) setGst(e.config.gst);
          if (e.config.termsText !== undefined) setTermsText(e.config.termsText || '');
          if (e.config.adminMarkup != null) setAdminMarkup(e.config.adminMarkup);
          if (e.config.clientMarkup != null) setClientMarkup(e.config.clientMarkup);
          if (e.config.minMarginAlert != null) setMinMarginAlert(e.config.minMarginAlert);
          if (e.config.extraOverheadPct != null) setExtraOverheadPct(e.config.extraOverheadPct);
          if (Array.isArray(e.config.appliedSuggestions)) setAppliedSuggestions(e.config.appliedSuggestions);
          // iter-296: restore hardware brands
          if (e.config.hingeBrand) setHingeBrand(e.config.hingeBrand);
          if (e.config.hingeBrandLabel) setHingeBrandLabel(e.config.hingeBrandLabel);
          if (e.config.straightenerBrand) setStraightenerBrand(e.config.straightenerBrand);
          if (e.config.straightenerBrandLabel) setStraightenerBrandLabel(e.config.straightenerBrandLabel);
          if (e.config.telescopicBrand) setTelescopicBrand(e.config.telescopicBrand);
          if (e.config.telescopicBrandLabel) setTelescopicBrandLabel(e.config.telescopicBrandLabel);
          if (e.config.slidingBrand) setSlidingBrand(e.config.slidingBrand);
          if (e.config.slidingBrandLabel) setSlidingBrandLabel(e.config.slidingBrandLabel);
          if (e.config.handleBrand) setHandleBrand(e.config.handleBrand);
          if (e.config.handleBrandLabel) setHandleBrandLabel(e.config.handleBrandLabel);
        }
        // iter-296: restore electrical & extras line items
        if (Array.isArray(e.electrical_items)) {
          setElectricalLines(prev => prev.map(line => {
            const saved = e.electrical_items.find(s => s.id === line.id);
            return saved ? { ...line, qty: +saved.qty || 0, rate: +saved.rate || line.rate } : line;
          }));
        }
        if (e.electrician_labor_pct != null) setElectricianLaborPct(+e.electrician_labor_pct);
        if (Array.isArray(e.extra_hardware_items)) {
          setExtraHardware(prev => prev.map(line => {
            const saved = e.extra_hardware_items.find(s => s.id === line.id);
            return saved ? { ...line, qty: +saved.qty || 0, rate: +saved.rate || line.rate } : line;
          }));
        }
        if (e.rates) {
          setPly18mm(e.rates.ply18mm ?? 58); setPly12mm(e.rates.ply12mm ?? 50); setPly8mm(e.rates.ply8mm ?? 30);
          setHdhmr16mm(e.rates.hdhmr16mm ?? 85); setInternalLaminateRate(e.rates.internalLaminateRate ?? 500);
          setExternalLaminateRate(e.rates.externalLaminateRate ?? 1800); setLaborCost(e.rates.laborCost ?? 450);
          setTelescopicDrawerCost(e.rates.telescopicDrawerCost ?? 450); setEdgeBandingRate(e.rates.edgeBandingRate ?? 7);
          setGlassProfileCost(e.rates.glassProfileCost ?? 1200); setTransportCost(e.rates.transportCost ?? 500);
          setDesignChargePercent(e.rates.designChargePercent ?? 5); setProfitMargin(e.rates.profitMargin ?? 10);
          // iter-297: restore per-estimate hardware rate overrides so BOQ
          // conversion picks up the actual hinge/straightener/sliding rates
          // entered in the Hardware Brands & Rates panel (previously these
          // fell back to backend defaults, distorting rates via scaling).
          if (e.rates.hingeRate != null) setHingeRate(+e.rates.hingeRate);
          if (e.rates.straightenerRate != null) setStraightenerRate(+e.rates.straightenerRate);
          if (e.rates.slidingChannelRate != null) setSlidingChannelRate(+e.rates.slidingChannelRate);
        }
      } catch { toast.error('Failed to load estimate'); }
    };
    loadEstimate();
  }, [estimateId]);

  // --- CALCULATIONS (Product-type aware) ---
  const productType = useMemo(() => {
    if (explicitProductType) return explicitProductType;
    const name = (estimateName || '').toLowerCase();
    if (name.includes('kitchen')) return 'kitchen';
    if (name.includes('gypsum')) return 'gypsum_panel';
    if (name.includes('moulding') || name.match(/\b(1|1\.5|2)["']?\s*(inch|in)?\s*moulding/)) return 'moulding';
    if (name.includes('panel') || name.includes('mdf panel') || name.includes('plywood panel') || name.includes('cladding')) return 'wall_panel';
    if (name.includes('bed') || name.includes('king') || name.includes('queen') || name.includes('single bed')) return 'bed';
    if (name.includes('tv') || name.includes('media') || name.includes('entertainment')) return 'tv_unit';
    if (name.includes('study') || name.includes('desk') || name.includes('bookshelf')) return 'study';
    if (name.includes('side table') || name.includes('nightstand') || name.includes('bedside')) return 'side_table';
    if (name.includes('walk-in') || name.includes('closet')) return 'walk_in';
    if (name.includes('loft')) return 'loft';
    return 'wardrobe';
  }, [estimateName, explicitProductType]);

  const isKitchen = productType === 'kitchen';

  // iter-304c (P4): when kitchen is selected, keep `kitchen.hardware_overrides`
  // in sync with the Hardware-Brands inputs so Material Summary + estimator
  // totals reflect the user's chosen rate immediately (no save required).
  useEffect(() => {
    if (!isKitchen) return;
    setKitchen(prev => {
      const cur = prev.hardware_overrides || {};
      const next = {
        ...cur,
        hingeRate: +hingeRate || 0,
        handleRate: +handleRate || 0,
        telescopicRate: +telescopicDrawerCost || 0,
        slidingRate: +slidingChannelRate || 0,
      };
      if (cur.hingeRate === next.hingeRate
          && cur.handleRate === next.handleRate
          && cur.telescopicRate === next.telescopicRate
          && cur.slidingRate === next.slidingRate) return prev;
      return { ...prev, hardware_overrides: next };
    });
  }, [isKitchen, hingeRate, handleRate, telescopicDrawerCost, slidingChannelRate]);
  const isMoulding = productType === 'moulding';
  const isPanelling = productType === 'wall_panel' || productType === 'gypsum_panel' || isMoulding;
  const isGypsum = productType === 'gypsum_panel';
  const isBed = productType === 'bed';
  const hasDrawers = !isPanelling && !isKitchen && drawerCount > 0;
  const hasShelves = !isPanelling && !isBed && !isKitchen && shelfCount > 0;
  const hasShutters = !isPanelling && !isBed && !isKitchen;

  const calculations = useMemo(() => {
    // ===== KITCHEN branch (full delegation to calculateKitchen) =====
    if (isKitchen) {
      const result = calculateKitchen(kitchen, {
        laborCost, transportCost, designChargePercent, profitMargin,
      });
      // iter-305 (P0): inject countertop & backsplash installation labour into
      // Material Summary as `kind: 'labour'` rows so the Material Summary IS the
      // single source of truth. Top tile (vendorBreakdown) will then derive its
      // 4 bucket totals from materialSummary[].kind → guaranteed 1:1 parity.
      const ctRunFt = +(kitchen.countertop?.runningFt) || 0;
      const ctDepthFt = +(kitchen.countertop?.depthFt) || 2;
      const ctArea = ctRunFt * ctDepthFt;
      const ctInstallCost = ctArea * countertopLaborRate;
      const bsRunFt = +(kitchen.backsplash?.runningFt) || 0;
      const bsHFt = +(kitchen.backsplash?.heightFt) || 0;
      const bsArea = bsRunFt * bsHFt;
      const bsInstallCost = bsArea * backsplashLaborRate;
      // Insert install labour rows right before design rows so the summary stays
      // grouped Material → Hardware → Labour → Design.
      const designIdx = result.materialSummary.findIndex(m => m.kind === 'design');
      const insertAt = designIdx >= 0 ? designIdx : result.materialSummary.length;
      const installRows = [];
      if (ctInstallCost > 0) installRows.push({ kind: 'labour', name: `Countertop Installation (${ctArea.toFixed(1)} sqft)`, used: ctArea, sheets: 1, rate_per_sheet: countertopLaborRate, unit_rate: countertopLaborRate, unit: 'sqft', total_cost: ctInstallCost, wastage: 0 });
      if (bsInstallCost > 0) installRows.push({ kind: 'labour', name: `Backsplash Installation (${bsArea.toFixed(1)} sqft)`, used: bsArea, sheets: 1, rate_per_sheet: backsplashLaborRate, unit_rate: backsplashLaborRate, unit: 'sqft', total_cost: bsInstallCost, wastage: 0 });
      if (installRows.length > 0) {
        result.materialSummary = [
          ...result.materialSummary.slice(0, insertAt),
          ...installRows,
          ...result.materialSummary.slice(insertAt),
        ];
      }
      return {
        ...result,
        drawerCount: 0,
        plywood18mmSheets: 0, plywood12mmSheets: 0, plywood8mmSheets: 0,
        hdhmrSheets: 0, internalLaminateSheets: 0, externalLaminateSheets: 0,
        shelfArea: 0, drawerPlywoodArea: 0,
        totalStraighteners: 0, straightenersPerShutter: 0, slidingChannelSets: 0,
        hingeTotalCost: 0, straightenerTotalCost: 0, slidingChannelTotalCost: 0,
        hingesPerShutter: 0, shutterWidthMm: 0,
        edgeBandingCost: 0, totalTelescopic: 0,
        carcassCost: result.carcassCost, shutterCost: result.shutterCost,
        laminateCost: 0,
      };
    }

    const sheetArea = 32; // 8x4 standard plywood sheet
    const gypsumSheetArea = 24; // 6x4 ft standard gypsum board
    const mm = (v) => Math.round(v * 304.8);
    const area = width * height;

    // ===== MOULDING-ONLY product type =====
    if (isMoulding) {
      const runningFeet = width; // width = running feet
      const mouldingTotal = runningFeet * mouldingRate;
      const laborTotal = runningFeet * 40; // Rs 40/ft labor for moulding install
      const transportTotal = transportCost;
      const baseCost = mouldingTotal + laborTotal + transportTotal;
      const designCharges = (baseCost * designChargePercent) / 100;
      const margin = (baseCost * profitMargin) / 100;
      const totalProjectCost = baseCost + designCharges + margin;

      const materialSummary = [
        { name: `${mouldingProfile}" Moulding Strip`, used: runningFeet, sheets: runningFeet, rate_per_sheet: mouldingRate, total_cost: mouldingTotal, wastage: 5 },
        { name: 'Adhesive / Nails', used: 1, sheets: 1, rate_per_sheet: Math.max(100, runningFeet * 5), total_cost: Math.max(100, runningFeet * 5), wastage: 0 },
      ];
      const cutList = [
        { code: 'M1', part: `${mouldingProfile}" Moulding Strip`, qty: Math.ceil(runningFeet / 8), w: mm(8), h: Math.round(mouldingProfile * 25.4), material: `${mouldingProfile}" Moulding`, laminate: 'Paint / Veneer' },
      ];

      return {
        area, shelfArea: 0, drawerPlywoodArea: 0, plywood18mmSheets: 0, plywood12mmSheets: 0, plywood8mmSheets: 0,
        hdhmrSheets: 0, internalLaminateSheets: 0, externalLaminateSheets: 0, totalEdgeBandingMeters: 0,
        totalHinges: 0, adhesiveKg: 0, drawerCount: 0,
        carcassCost: mouldingTotal, shutterCost: 0, laminateCost: 0, edgeBandingCost: 0,
        totalTelescopic: 0, totalLabor: laborTotal, transportCost: transportTotal, glassTotal: 0,
        baseCost, designCharges, margin, totalProjectCost,
        materialSummary, cutList, productType, runningFeet, mouldingProfile,
      };
    }

    const shelfDepth = depth - 0.5;
    const shelfArea = hasShelves ? shelfCount * width * shelfDepth : 0;

    const dWidth = drawerWidthType === 'full' ? width : width / 2;
    const dDepth = depth - 0.5;
    const dHeightFt = drawerHeight / 12;
    const singleDrawerArea = (dHeightFt * dDepth * 2) + (dHeightFt * dWidth * 2) + (dWidth * dDepth);
    const drawerPlywoodArea = hasDrawers ? singleDrawerArea * drawerCount : 0;

    // Material areas (adapt to product type)
    let plywood18mmArea, plywood8mmArea;
    if (isPanelling) {
      // Panelling: only main panels, no back/shelves/drawers/shutters
      plywood18mmArea = area;
      plywood8mmArea = 0;
    } else if (isBed) {
      // Bed: headboard + side rails + base
      plywood18mmArea = area + (width * depth); // headboard + base
      plywood8mmArea = width * depth; // base bottom
    } else {
      plywood18mmArea = area + shelfArea;
      plywood8mmArea = area; // back panel
    }
    const plywood18mmSheets = Math.ceil(plywood18mmArea / sheetArea);
    const plywood12mmSheets = hasDrawers ? Math.ceil(drawerPlywoodArea / sheetArea) : 0;
    const plywood8mmSheets = plywood8mmArea > 0 ? Math.ceil(plywood8mmArea / sheetArea) : 0;
    const hdhmrSheets = hasShutters ? Math.ceil(area / sheetArea) : 0;

    // Laminate: back panel gets Internal (not raw)
    const internalLaminateArea = area + (shelfArea * 2) + (drawerPlywoodArea * 2) + (plywood8mmArea > 0 ? plywood8mmArea : 0);
    const internalLaminateSheets = isGypsum ? 0 : Math.ceil(internalLaminateArea / sheetArea);
    const externalLaminateArea = hasShutters ? area + (2 * height) + width : (isPanelling && !isGypsum ? area : 0);
    const externalLaminateSheets = isGypsum ? 0 : Math.ceil(externalLaminateArea / sheetArea);

    // Edge banding
    const shelfEdgeBanding = hasShelves ? shelfCount * (2 * width + 2 * shelfDepth) * 0.3048 : 0;
    const totalEdgeBandingMeters = isPanelling ? 0 : (((2 * height) + (2 * width)) * 0.3048 * 2) + shelfEdgeBanding;

    // ===== SMART SHUTTER CALCULATION (industry standards) =====
    // Shutter width constraints: min 300mm (12"), max 600mm (24")
    // For a wardrobe width, split into equal shutters keeping each within 300–600mm
    const widthMm = width * 304.8;
    const heightMm = height * 304.8;
    const heightInches = height * 12;
    let shutterCount = 0;
    let shutterWidthMm = 0;
    if (hasShutters) {
      shutterCount = Math.max(1, Math.ceil(widthMm / 600)); // split at 600mm max per shutter
      shutterWidthMm = widthMm / shutterCount;
      // Enforce min 300mm: if too narrow, reduce count (rare: only for tiny wardrobes)
      while (shutterCount > 1 && (widthMm / (shutterCount - 1)) <= 600 && shutterWidthMm < 300) {
        shutterCount -= 1;
        shutterWidthMm = widthMm / shutterCount;
      }
    }

    // Hinges per shutter based on shutter height (industry rule: 1 hinge per 30" of height)
    // ≤36" → 2, ≤60" → 3, ≤84" → 4, ≤96" → 5
    const hingesPerShutter = heightInches <= 36 ? 2 : heightInches <= 60 ? 3 : heightInches <= 84 ? 4 : 5;
    const totalHinges = hasShutters && wardrobeType === 'hinged' ? shutterCount * hingesPerShutter : 0;
    const hingeTotalCost = totalHinges * hingeRate;

    // Door Straighteners (anti-warp)
    // Mandatory: shutter height > 2100mm (7ft). Width > 560mm (22") → 2 per shutter.
    let straightenersPerShutter = 0;
    if (hasShutters) {
      if (heightMm > 2100) straightenersPerShutter = 1;
      if (shutterWidthMm > 560) straightenersPerShutter = Math.max(straightenersPerShutter, 2);
      // Sliding systems → recommended 1 per shutter minimum
      if (wardrobeType === 'sliding' && straightenersPerShutter === 0) straightenersPerShutter = 1;
    }
    const totalStraighteners = shutterCount * straightenersPerShutter;
    const straightenerTotalCost = totalStraighteners * straightenerRate;

    // Sliding Channels (only for sliding wardrobes) — 1 set per linear meter of track
    const slidingChannelSets = (hasShutters && wardrobeType === 'sliding') ? Math.max(1, Math.ceil(width * 0.3048)) : 0;
    const slidingChannelTotalCost = slidingChannelSets * slidingChannelRate;

    const hardwareCost = hingeTotalCost + straightenerTotalCost + slidingChannelTotalCost;
    const adhesiveKg = internalLaminateSheets + externalLaminateSheets;

    // Gypsum-specific costs (6x4 ft sheet = 24 sqft)
    const gypsumSheets = isGypsum ? Math.ceil(area / gypsumSheetArea) : 0;
    const gypsumRate = 45; // Rs/sqft
    const paintRate = 25; // Rs/sqft (2 coats primer + paint)
    const gypsumCost = isGypsum ? area * gypsumRate : 0;
    const paintCost = isGypsum ? area * paintRate : 0;

    // Costs
    const carcassCost = isGypsum ? gypsumCost : plywood18mmArea * ply18mm;
    const shutterCost = hasShutters ? area * hdhmr16mm : 0;
    const laminateCost = isGypsum ? paintCost : (internalLaminateSheets * internalLaminateRate) + (externalLaminateSheets * externalLaminateRate);
    const edgeBandingCost = totalEdgeBandingMeters * edgeBandingRate;
    const totalTelescopic = hasDrawers ? telescopicDrawerCost * drawerCount : 0;
    const totalLabor = area * laborCost;
    const glassTotal = includeGlassProfile ? glassProfileCost : 0;

    // iter-296: Electrical & extra hardware totals (per-line qty * rate). Both
    // are excluded if the product type is not a wardrobe-style storage unit
    // (panelling/moulding/bed don't surface electrical inputs).
    const electricalMaterial = !isKitchen && !isMoulding
      ? electricalLines.reduce((s, l) => s + ((+l.qty || 0) * (+l.rate || 0)), 0)
      : 0;
    const electricianLabor = (electricalMaterial * (+electricianLaborPct || 0)) / 100;
    const extraHardwareTotal = !isKitchen && !isMoulding
      ? extraHardware.reduce((s, l) => s + ((+l.qty || 0) * (+l.rate || 0)), 0)
      : 0;

    const baseCost = carcassCost + shutterCost + laminateCost + edgeBandingCost + totalTelescopic + totalLabor + transportCost + hardwareCost + electricalMaterial + electricianLabor + extraHardwareTotal;
    const designCharges = (baseCost * designChargePercent) / 100;
    const margin = (baseCost * profitMargin) / 100;
    const totalProjectCost = baseCost + designCharges + margin + glassTotal;

    // Material summary (adapt to product type)
    const materialSummary = [];
    if (isGypsum) {
      materialSummary.push({ name: "Gypsum Board (6'x4' sheet)", used: area, sheets: gypsumSheets, rate_per_sheet: gypsumRate * gypsumSheetArea, total_cost: gypsumCost });
      materialSummary.push({ name: 'Paint (Primer + 2 Coats)', used: area, sheets: Math.ceil(area / 100), rate_per_sheet: paintRate * 100, total_cost: paintCost });
    } else {
      if (plywood18mmArea > 0) materialSummary.push({ name: isPanelling ? '18mm Plywood (Panels)' : isBed ? '18mm Plywood (Frame)' : '18mm Plywood (Carcass)', used: plywood18mmArea, sheets: plywood18mmSheets, rate_per_sheet: ply18mm * sheetArea, total_cost: plywood18mmSheets * ply18mm * sheetArea });
      if (plywood12mmSheets > 0) materialSummary.push({ name: '12mm Plywood (Drawers)', used: drawerPlywoodArea, sheets: plywood12mmSheets, rate_per_sheet: ply12mm * sheetArea, total_cost: plywood12mmSheets * ply12mm * sheetArea });
      if (plywood8mmSheets > 0) materialSummary.push({ name: '8mm Plywood (Back Panel)', used: plywood8mmArea, sheets: plywood8mmSheets, rate_per_sheet: ply8mm * sheetArea, total_cost: plywood8mmSheets * ply8mm * sheetArea });
      if (hdhmrSheets > 0) materialSummary.push({ name: '16mm HDHMR (Shutters)', used: area, sheets: hdhmrSheets, rate_per_sheet: hdhmr16mm * sheetArea, total_cost: hdhmrSheets * hdhmr16mm * sheetArea });
      if (internalLaminateSheets > 0) materialSummary.push({ name: 'Internal Laminate', used: internalLaminateArea, sheets: internalLaminateSheets, rate_per_sheet: internalLaminateRate, total_cost: internalLaminateSheets * internalLaminateRate });
      if (externalLaminateSheets > 0) materialSummary.push({ name: isPanelling ? 'Laminate Finish' : 'External Laminate', used: externalLaminateArea, sheets: externalLaminateSheets, rate_per_sheet: externalLaminateRate, total_cost: externalLaminateSheets * externalLaminateRate });
    }
    materialSummary.forEach(m => { m.wastage = ((m.sheets * (m.name.includes('Gypsum') ? gypsumSheetArea : sheetArea) - m.used) / (m.used || 1)) * 100; });

    // Hardware line items (hinges, straighteners, sliding channels)
    if (totalHinges > 0) materialSummary.push({ name: `Hinges (${hingesPerShutter}/shutter, ${shutterCount} shutters)`, used: totalHinges, sheets: totalHinges, rate_per_sheet: hingeRate, total_cost: hingeTotalCost, wastage: 0 });
    if (totalStraighteners > 0) materialSummary.push({ name: `Door Straighteners (anti-warp)`, used: totalStraighteners, sheets: totalStraighteners, rate_per_sheet: straightenerRate, total_cost: straightenerTotalCost, wastage: 0 });
    if (slidingChannelSets > 0) materialSummary.push({ name: `Sliding Channel Set (${slidingChannelSets}m track)`, used: slidingChannelSets, sheets: slidingChannelSets, rate_per_sheet: slidingChannelRate, total_cost: slidingChannelTotalCost, wastage: 0 });

    // Cut list (adapt to product type — no drawers for panelling, no shutters for beds etc.)
    const cutList = [];
    if (isPanelling) {
      // Panelling: just panel sections
      const panelCount = Math.ceil(width / 4); // 4ft wide panels
      for (let i = 0; i < panelCount; i++) {
        const pw = i < panelCount - 1 ? 4 : width - (panelCount - 1) * 4;
        cutList.push({ code: `P${i+1}`, part: `Panel Section ${i+1}`, qty: 1, w: mm(pw), h: mm(height), material: isGypsum ? 'Gypsum Board' : '18mm Plywood', laminate: isGypsum ? 'Paint' : 'Laminate' });
      }
    } else {
      // Standard furniture: carcass
      cutList.push({ code: 'C1', part: 'Left Side Panel', qty: 1, w: mm(depth), h: mm(height), material: '18mm Plywood', laminate: 'Internal' });
      cutList.push({ code: 'C2', part: 'Right Side Panel', qty: 1, w: mm(depth), h: mm(height), material: '18mm Plywood', laminate: 'Internal' });
      cutList.push({ code: 'C3', part: isBed ? 'Headboard' : 'Top Panel', qty: 1, w: mm(width), h: mm(isBed ? height : depth), material: '18mm Plywood', laminate: isBed ? 'External' : 'Internal' });
      cutList.push({ code: 'C4', part: isBed ? 'Footboard' : 'Bottom Panel', qty: 1, w: mm(width), h: mm(isBed ? height * 0.6 : depth), material: '18mm Plywood', laminate: isBed ? 'External' : 'Internal' });
      if (!isBed) cutList.push({ code: 'C5', part: 'Back Panel', qty: 1, w: mm(width), h: mm(height), material: '8mm Plywood', laminate: 'Internal' });
      if (isBed) cutList.push({ code: 'C5', part: 'Base Platform', qty: 1, w: mm(width), h: mm(depth), material: '18mm Plywood', laminate: 'Internal' });
      // Shelves
      if (hasShelves) {
        for (let i = 0; i < shelfCount; i++) {
          cutList.push({ code: `S${i+1}`, part: `Shelf ${i+1}`, qty: 1, w: mm(width), h: mm(shelfDepth), material: '18mm Plywood', laminate: 'Internal' });
        }
      }
      // Drawers
      if (hasDrawers) {
        for (let i = 0; i < drawerCount; i++) {
          cutList.push({ code: `D${i+1}-L`, part: `Drawer ${i+1} Left Side`, qty: 1, w: mm(dDepth), h: mm(dHeightFt), material: '12mm Plywood', laminate: 'Internal' });
          cutList.push({ code: `D${i+1}-R`, part: `Drawer ${i+1} Right Side`, qty: 1, w: mm(dDepth), h: mm(dHeightFt), material: '12mm Plywood', laminate: 'Internal' });
          cutList.push({ code: `D${i+1}-F`, part: `Drawer ${i+1} Front`, qty: 1, w: mm(dWidth), h: mm(dHeightFt), material: '12mm Plywood', laminate: 'Internal' });
          cutList.push({ code: `D${i+1}-B`, part: `Drawer ${i+1} Back`, qty: 1, w: mm(dWidth), h: mm(dHeightFt), material: '12mm Plywood', laminate: 'Internal' });
          cutList.push({ code: `D${i+1}-BT`, part: `Drawer ${i+1} Bottom`, qty: 1, w: mm(dWidth), h: mm(dDepth), material: '8mm Plywood', laminate: 'Internal' });
        }
      }
      // Shutters (not for beds/panelling) — smart split: each shutter 300–600mm wide
      if (hasShutters && shutterCount > 0) {
        for (let i = 0; i < shutterCount; i++) {
          cutList.push({
            code: `SH${i + 1}`,
            part: `Shutter ${i + 1} of ${shutterCount}`,
            qty: 1,
            w: Math.round(shutterWidthMm),
            h: mm(height),
            material: '16mm HDHMR',
            laminate: 'External',
          });
        }
      }
    }

    return {
      area, shelfArea, drawerPlywoodArea, plywood18mmSheets, plywood12mmSheets, plywood8mmSheets,
      hdhmrSheets, internalLaminateSheets, externalLaminateSheets, totalEdgeBandingMeters,
      totalHinges, adhesiveKg, drawerCount: hasDrawers ? drawerCount : 0, carcassCost, shutterCost, laminateCost,
      edgeBandingCost, totalTelescopic, totalLabor, transportCost, glassTotal,
      baseCost, designCharges, margin, totalProjectCost,
      materialSummary, cutList, productType,
      // Smart shutter + hardware
      shutterCount, shutterWidthMm, hingesPerShutter,
      totalStraighteners, straightenersPerShutter, slidingChannelSets,
      hingeTotalCost, straightenerTotalCost, slidingChannelTotalCost, hardwareCost,
      // iter-296: electrical + extras (per-estimate breakdown)
      electricalMaterial, electricianLabor, extraHardwareTotal,
    };
  }, [width, height, depth, shelfCount, drawerCount, drawerWidthType, drawerHeight,
    ply18mm, ply12mm, ply8mm, hdhmr16mm, internalLaminateRate, externalLaminateRate,
    laborCost, telescopicDrawerCost, edgeBandingRate, glassProfileCost, includeGlassProfile,
    transportCost, designChargePercent, profitMargin, wardrobeType, isPanelling, isGypsum, isBed,
    isMoulding, mouldingProfile, mouldingRate,
    hingeRate, slidingChannelRate, straightenerRate,
    hasDrawers, hasShelves, hasShutters, productType,
    isKitchen, kitchen, countertopLaborRate, backsplashLaborRate,
    // iter-296: re-compute when any electrical / extra-hardware state changes
    electricalLines, electricianLaborPct, extraHardware]);

  // === Unified Vendor Price (same formula used in Design card + Cost Sheet mirror) ===
  // Ensures "Estimated Project Cost" numbers always match between both tabs.
  const vendorBreakdown = useMemo(() => {
    const matRows = calculations.materialSummary || [];
    // iter-305 (P0): KITCHEN — derive 4 buckets directly from materialSummary[].kind
    // tags. Material Summary IS the single source of truth — top tile totals
    // therefore MUST equal Material Summary section subtotals exactly.
    if (isKitchen) {
      const byKind = (k) => matRows
        .filter(m => (m.kind || 'material') === k)
        .reduce((a, m) => a + (+m.total_cost || 0), 0);
      return {
        material: byKind('material'),
        hardware: byKind('hardware'),
        labour: byKind('labour'),
        designCharges: byKind('design'),
      };
    }
    // STORAGE / WARDROBE — legacy regex-based split (materialSummary doesn't carry `kind`).
    const HARDWARE_NAME_RE = /(hinge|handle|straightener|sliding channel|drawer slide|tandem|trolley|corner system|lift-?up|stay)/i;
    const material = matRows.filter(m => !HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
    const hardware = matRows.filter(m => HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
    const labour = (calculations.totalLabor || 0) + (calculations.electricianLabor || 0);
    const designCharges = calculations.designCharges || 0;
    const electricalMaterial = calculations.electricalMaterial || 0;
    const extras = calculations.extraHardwareTotal || 0;
    return {
      material: material + electricalMaterial,
      hardware: hardware + extras,
      labour,
      designCharges,
    };
  }, [calculations, isKitchen]);
  const vendorProjectCost = vendorBreakdown.material + vendorBreakdown.hardware + vendorBreakdown.labour + vendorBreakdown.designCharges;

  // === Final Client Price (single source of truth) — overheads applied on CLIENT side ===
  // Flow: Vendor (clean) → Admin Markup → Client Pre-Discount → +Overheads → Discount → GST → Final.
  const finalPricing = useMemo(() => {
    const vendor = vendorProjectCost;
    const totalInternal = vendor - vendorBreakdown.designCharges;
    const withExtraOverhead = totalInternal * (1 + (+extraOverheadPct || 0) / 100);
    const vendorWithOverhead = withExtraOverhead + vendorBreakdown.designCharges;
    const adminPrice = vendorWithOverhead * (1 + (+adminMarkup || 0) / 100);
    const clientBase = adminPrice * (1 + (+clientMarkup || 0) / 100);
    // Client-side overheads (freight / labour handling / consultancy as % of Client Base).
    const freightAmt = clientBase * ((+overhead.freight || 0) / 100);
    const handlingAmt = clientBase * ((+overhead.labour_handling || 0) / 100);
    const consultAmt = clientBase * ((+overhead.consultancy || 0) / 100);
    const overheadsTotal = freightAmt + handlingAmt + consultAmt;
    const clientPreDiscount = clientBase + overheadsTotal;
    const discAmt = discount.type === 'percent' ? clientPreDiscount * ((+discount.value || 0) / 100) : (+discount.value || 0);
    const clientPreGst = Math.max(0, clientPreDiscount - discAmt);
    const gstAmt = gst.type === 'percent' ? clientPreGst * ((+gst.value || 0) / 100) : (+gst.value || 0);
    const finalClient = clientPreGst + gstAmt;
    const marginPct = clientPreGst > 0 ? ((clientPreGst - vendor) / clientPreGst) * 100 : 0;
    return {
      vendor, adminPrice, clientBase, clientPreDiscount, discAmt, clientPreGst, gstAmt, finalClient,
      marginPct, lowMargin: marginPct < minMarginAlert,
      freightAmt, handlingAmt, consultAmt, overheadsTotal,
    };
  }, [vendorProjectCost, vendorBreakdown.designCharges, adminMarkup, clientMarkup, extraOverheadPct, overhead, discount, gst, minMarginAlert]);

  // === iter-301: 3-Tier Quote Comparison ===
  // Compute finalPricing for each Brand Portfolio preset using the CURRENT
  // estimate's carcass/shutter/laminate/electrical/extras costs (same across
  // tiers) while swapping only the 5 hardware rates. Mirrors finalPricing()
  // math so grand totals line up with what the client sees per tier.
  const tierSummaries = useMemo(() => {
    if (isKitchen) return [];
    const totalHinges = +calculations.totalHinges || 0;
    const totalStraighteners = +calculations.totalStraighteners || 0;
    const slidingSets = +calculations.slidingChannelSets || 0;
    const drawers = +drawerCount || 0;
    const handlesN = +calculations.shutterCount || 0;
    // Current tier's hardware cost (what's already in vendorBreakdown.hardware)
    const currentHwCost =
      (+hingeRate * totalHinges)
      + (+straightenerRate * totalStraighteners)
      + (+telescopicDrawerCost * drawers)
      + (+slidingChannelRate * slidingSets)
      + (+handleRate * handlesN);

    return BRAND_PORTFOLIO_PRESETS.map(preset => {
      const rp = resolvePreset(preset.id)?.resolved || {};
      const rates = {
        hinge: rp.hinge?.rate || 0,
        straightener: rp.straightener?.rate || 0,
        telescopic: rp.telescopic?.rate || 0,
        sliding_channel: rp.sliding_channel?.rate || 0,
        handle: rp.handle?.rate || 0,
      };
      const tierHwCost =
        (rates.hinge * totalHinges)
        + (rates.straightener * totalStraighteners)
        + (rates.telescopic * drawers)
        + (rates.sliding_channel * slidingSets)
        + (rates.handle * handlesN);
      const vendor = vendorProjectCost + (tierHwCost - currentHwCost);
      // Mirror finalPricing flow
      const totalInternal = vendor - vendorBreakdown.designCharges;
      const withExtraOverhead = totalInternal * (1 + (+extraOverheadPct || 0) / 100);
      const vendorWithOverhead = withExtraOverhead + vendorBreakdown.designCharges;
      const adminPrice = vendorWithOverhead * (1 + (+adminMarkup || 0) / 100);
      const clientBase = adminPrice * (1 + (+clientMarkup || 0) / 100);
      const freightAmt = clientBase * ((+overhead.freight || 0) / 100);
      const handlingAmt = clientBase * ((+overhead.labour_handling || 0) / 100);
      const consultAmt = clientBase * ((+overhead.consultancy || 0) / 100);
      const clientPreDiscount = clientBase + freightAmt + handlingAmt + consultAmt;
      const discAmt = discount.type === 'percent'
        ? clientPreDiscount * ((+discount.value || 0) / 100)
        : (+discount.value || 0);
      const clientPreGst = Math.max(0, clientPreDiscount - discAmt);
      const gstAmt = gst.type === 'percent' ? clientPreGst * ((+gst.value || 0) / 100) : (+gst.value || 0);
      const finalClient = clientPreGst + gstAmt;
      const marginPct = clientPreGst > 0 ? ((clientPreGst - vendor) / clientPreGst) * 100 : 0;
      return {
        id: preset.id, label: preset.label, tag: preset.tag, tone: preset.tone,
        brands: {
          hinge: rp.hinge?.label, straightener: rp.straightener?.label,
          telescopic: rp.telescopic?.label, sliding: rp.sliding_channel?.label,
          handle: rp.handle?.label,
        },
        rates,
        hardwareCost: tierHwCost,
        vendor,
        clientBase,
        clientPreGst,
        gstAmt,
        finalClient,
        marginPct,
      };
    });
  }, [isKitchen, calculations, drawerCount, hingeRate, straightenerRate,
    telescopicDrawerCost, slidingChannelRate, handleRate, vendorProjectCost,
    vendorBreakdown.designCharges, extraOverheadPct, adminMarkup, clientMarkup,
    overhead, discount, gst]);

  // === iter-299: Live BOQ Preview ===
  // Mirrors /app/backend/routers/estimates.py::_build_boq_items_for_estimate
  // so designers see exactly what the BOQ will contain BEFORE converting.
  // Scaling factor = (1+adminMarkup)(1+clientMarkup) — same as backend when
  // raw_items_sum ≈ totalProjectCost (iter-297 parity baseline).
  const boqPreviewItems = useMemo(() => {
    if (isKitchen) return []; // Kitchen preview not handled here.
    const wMm = Math.round((+width || 0) * 304.8);
    const hMm = Math.round((+height || 0) * 304.8);
    const faceSqft = (wMm * hMm) / 1_000_000 * 10.7639;
    const moduleVendor = (
      (+calculations.carcassCost || 0)
      + (+calculations.shutterCost || 0)
      + (+calculations.laminateCost || 0)
      + (+calculations.edgeBandingCost || 0)
      + (+calculations.transportCost || 0)
      + (+calculations.totalLabor || 0)
      + (+calculations.designCharges || 0)
      + (+calculations.margin || 0)
      + (+calculations.glassTotal || 0)
    );
    const scale = (1 + (+adminMarkup || 0) / 100) * (1 + (+clientMarkup || 0) / 100);
    const items = [];
    items.push({
      section: 'Module Breakdown',
      name: estimateName || 'Storage Module',
      qty: faceSqft > 0 ? +faceSqft.toFixed(2) : 1,
      unit: faceSqft > 0 ? 'Sqft' : 'Module',
      vendorRate: faceSqft > 0 ? moduleVendor / faceSqft : moduleVendor,
      clientRate: (faceSqft > 0 ? moduleVendor / faceSqft : moduleVendor) * scale,
    });
    const totalHinges = +calculations.totalHinges || 0;
    if (totalHinges > 0) items.push({ section: 'Handles & Hardware', name: `Hinges${hingeBrandLabel ? ` (${hingeBrandLabel})` : ' (Soft-Close)'}`, qty: totalHinges, unit: 'Nos', vendorRate: +hingeRate, clientRate: +hingeRate * scale });
    const totalStraighteners = +calculations.totalStraighteners || 0;
    if (totalStraighteners > 0) items.push({ section: 'Handles & Hardware', name: `Door Straighteners${straightenerBrandLabel ? ` (${straightenerBrandLabel})` : ''}`, qty: totalStraighteners, unit: 'Nos', vendorRate: +straightenerRate, clientRate: +straightenerRate * scale });
    const slidingSets = +calculations.slidingChannelSets || 0;
    if (slidingSets > 0) items.push({ section: 'Handles & Hardware', name: `Sliding Channel Set${slidingBrandLabel ? ` (${slidingBrandLabel})` : ''}`, qty: slidingSets, unit: 'Set', vendorRate: +slidingChannelRate, clientRate: +slidingChannelRate * scale });
    const drawers = +drawerCount || 0;
    if (drawers > 0) items.push({ section: 'Handles & Hardware', name: `Telescopic Drawer Slides${telescopicBrandLabel ? ` (${telescopicBrandLabel})` : ''}`, qty: drawers, unit: 'Pair', vendorRate: +telescopicDrawerCost, clientRate: +telescopicDrawerCost * scale });
    const hCount = +calculations.shutterCount || 0;
    if (hCount > 0) items.push({ section: 'Handles & Hardware', name: `Handles${handleBrandLabel ? ` (${handleBrandLabel})` : ''}`, qty: hCount, unit: 'Nos', vendorRate: +handleRate, clientRate: +handleRate * scale });
    electricalLines.filter(l => +l.qty > 0 && +l.rate > 0).forEach(l => {
      items.push({ section: 'Electrical', name: l.label, qty: +l.qty, unit: l.unit || 'Nos', vendorRate: +l.rate, clientRate: +l.rate * scale });
    });
    extraHardware.filter(l => +l.qty > 0 && +l.rate > 0).forEach(l => {
      items.push({ section: 'Additional Hardware', name: l.label, qty: +l.qty, unit: l.unit || 'Nos', vendorRate: +l.rate, clientRate: +l.rate * scale });
    });
    return items;
  }, [isKitchen, width, height, calculations, adminMarkup, clientMarkup, estimateName,
    hingeRate, straightenerRate, slidingChannelRate, telescopicDrawerCost, handleRate, drawerCount,
    hingeBrandLabel, straightenerBrandLabel, slidingBrandLabel, telescopicBrandLabel, handleBrandLabel,
    electricalLines, extraHardware]);

  // --- SAVE ---
  const handleSave = async () => {
    setSaving(true);
    try {
      // Compute per-module cost in the SAME formula as buildQuotePayload so that
      // the BOQ converter re-uses the exact rate (single-source-of-truth guarantee).
      const kitchenForSave = isKitchen ? (() => {
        const LIBS = { base: BASE_UNITS, wall: WALL_UNITS, tall: TALL_UNITS };
        const mm2sqft = (w, h) => (w * h) / (304.8 * 304.8);
        const withCosts = (kitchen.modules || []).map(m => {
          const def = (LIBS[m.category] || []).find(x => x.id === m.type);
          const carcassArea = (mm2sqft(m.depthMm, m.heightMm) * 2) + (mm2sqft(m.widthMm, m.depthMm) * 2) + mm2sqft(m.widthMm, m.heightMm);
          const carcassRate = CARCASS_BY_ID[m.carcass]?.rate || 75;
          const shutterArea = def?.hasShutter ? mm2sqft(m.widthMm, m.heightMm) : 0;
          const shutterRate = SHUTTER_BY_ID[m.shutter]?.rate || 140;
          const cost = ((carcassArea * carcassRate) + (shutterArea * shutterRate)) * (m.qty || 1);
          return { ...m, cost };
        });
        return { ...kitchen, modules: withCosts };
      })() : kitchen;

      const payload = {
        name: estimateName,
        project_id: linkedProject || null,
        client_id: linkedClient || null,
        dimensions: { width, height, depth },
        config: { wardrobeType, shelfCount, drawerCount, drawerWidthType, drawerHeight, includeGlassProfile, productType, kitchen: isKitchen ? kitchenForSave : undefined, handleProfile, handleRate, countertopLaborRate, backsplashLaborRate, overhead, discount, gst, termsText, adminMarkup, clientMarkup, minMarginAlert, extraOverheadPct, appliedSuggestions, hingeBrand, hingeBrandLabel, straightenerBrand, straightenerBrandLabel, telescopicBrand, telescopicBrandLabel, slidingBrand, slidingBrandLabel, handleBrand, handleBrandLabel },
        rates: { ply18mm, ply12mm, ply8mm, hdhmr16mm, internalLaminateRate, externalLaminateRate, laborCost, telescopicDrawerCost, edgeBandingRate, glassProfileCost, transportCost, designChargePercent, profitMargin, hingeRate, straightenerRate, slidingChannelRate },
        calculations: calculations,
        cut_list: calculations.cutList,
        material_summary: calculations.materialSummary,
        total_cost: finalPricing.finalClient,
        // Persist the client-facing pre-GST price so BOQ conversion scales items identically to the Kitchen PDF.
        client_price_pre_gst: finalPricing.clientPreGst,
        // P0 — also persist the pre-overhead Client Base so BOQ converter can scale
        // line items to the PRE-OVERHEAD basic and emit overheads as separate BOQ
        // line items. This guarantees exact per-line-rate parity with the Kitchen
        // Estimator (no more 14% rate drift).
        client_base_pre_overheads: finalPricing.clientBase,
        overhead_breakdown: {
          freight: Math.round(finalPricing.freightAmt || 0),
          labour_handling: Math.round(finalPricing.handlingAmt || 0),
          consultancy: Math.round(finalPricing.consultAmt || 0),
          freight_pct: +overhead.freight || 0,
          labour_handling_pct: +overhead.labour_handling || 0,
          consultancy_pct: +overhead.consultancy || 0,
        },
        gst_amount: finalPricing.gstAmt,
        notes,
        // iter-296: persist Storage Estimator electrical & extras so they
        // survive reload + propagate into BOQ conversion + PDF generation.
        electrical_items: !isKitchen ? electricalLines.filter(l => +l.qty > 0).map(l => ({
          id: l.id, name: l.label, qty: +l.qty, unit: l.unit,
          rate: +l.rate, amount: (+l.qty || 0) * (+l.rate || 0),
        })) : [],
        electrician_labor_pct: !isKitchen ? +electricianLaborPct : 0,
        extra_hardware_items: !isKitchen ? extraHardware.filter(l => +l.qty > 0).map(l => ({
          id: l.id, name: l.label, qty: +l.qty, unit: l.unit,
          rate: +l.rate, amount: (+l.qty || 0) * (+l.rate || 0),
        })) : [],
      };
      if (existingEstimate) {
        await axios.put(`${API}/estimates/${existingEstimate.id}`, payload, { headers });
        toast.success('Estimate updated (new revision saved)');
      } else {
        let res;
        try {
          res = await axios.post(`${API}/estimates`, payload, { headers });
        } catch (err) {
          // Kitchen 5-cap reached → ask user to confirm auto-purge of oldest.
          const detail = err.response?.data?.detail;
          if (err.response?.status === 409 && detail && detail.code === 'KITCHEN_CAP_REACHED') {
            const oldest = detail.oldest || {};
            const ok = window.confirm(
              `You've reached the ${detail.max}-kitchen storage limit.\n\n` +
              `Saving this new kitchen will auto-delete the OLDEST kitchen:\n` +
              `   "${oldest.name || 'Unknown'}" (created ${oldest.created_at ? new Date(oldest.created_at).toLocaleDateString() : '—'})\n\n` +
              `Continue?`
            );
            if (!ok) { setSaving(false); return; }
            res = await axios.post(`${API}/estimates`, { ...payload, force_delete_oldest_kitchen: true }, { headers });
            if (res.data?._purged_kitchen_name) {
              toast.info(`Oldest kitchen "${res.data._purged_kitchen_name}" auto-deleted to stay within the ${detail.max}-kitchen cap.`);
            }
          } else {
            throw err;
          }
        }
        setExistingEstimate(res.data);
        navigate(`/estimators/${isKitchen ? 'kitchen' : 'wardrobe'}/${res.data.id}`, { replace: true });
        toast.success('Estimate saved');
      }
    } catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // --- DUPLICATE (A/B variant) ---
  const duplicateEstimate = async () => {
    try {
      setSaving(true);
      const payload = {
        name: `${estimateName} (Copy)`,
        project_id: linkedProject || null,
        client_id: linkedClient || null,
        dimensions: { width, height, depth },
        config: { wardrobeType, shelfCount, drawerCount, drawerWidthType, drawerHeight, includeGlassProfile, productType, kitchen: isKitchen ? kitchen : undefined, handleProfile, handleRate, countertopLaborRate, backsplashLaborRate, overhead, discount, gst, termsText, adminMarkup, clientMarkup, minMarginAlert, extraOverheadPct, appliedSuggestions },
        rates: { ply18mm, ply12mm, ply8mm, hdhmr16mm, internalLaminateRate, externalLaminateRate, laborCost, telescopicDrawerCost, edgeBandingRate, glassProfileCost, transportCost, designChargePercent, profitMargin, hingeRate, straightenerRate, slidingChannelRate },
        calculations, cut_list: calculations.cutList, material_summary: calculations.materialSummary,
        total_cost: finalPricing.finalClient,
        client_price_pre_gst: finalPricing.clientPreGst,
        client_base_pre_overheads: finalPricing.clientBase,
        overhead_breakdown: {
          freight: Math.round(finalPricing.freightAmt || 0),
          labour_handling: Math.round(finalPricing.handlingAmt || 0),
          consultancy: Math.round(finalPricing.consultAmt || 0),
          freight_pct: +overhead.freight || 0,
          labour_handling_pct: +overhead.labour_handling || 0,
          consultancy_pct: +overhead.consultancy || 0,
        },
        gst_amount: finalPricing.gstAmt,
        notes: notes ? `${notes}\n\n[Duplicated from original]` : '[Duplicated from original]',
      };
      let res;
      try {
        res = await axios.post(`${API}/estimates`, payload, { headers });
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 409 && detail && detail.code === 'KITCHEN_CAP_REACHED') {
          const oldest = detail.oldest || {};
          const ok = window.confirm(
            `You've reached the ${detail.max}-kitchen storage limit.\n\n` +
            `Duplicating will auto-delete the OLDEST kitchen "${oldest.name || 'Unknown'}".\n\nContinue?`
          );
          if (!ok) { setSaving(false); return; }
          res = await axios.post(`${API}/estimates`, { ...payload, force_delete_oldest_kitchen: true }, { headers });
        } else {
          throw err;
        }
      }
      toast.success('Duplicated — opening new copy');
      navigate(`/estimators/${isKitchen ? 'kitchen' : 'wardrobe'}/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to duplicate'); }
    finally { setSaving(false); }
  };

  // --- CONVERT TO BOQ ---
  const handleConvertToBOQ = async () => {
    if (!existingEstimate) { toast.error('Save the estimate first'); return; }
    if (!linkedProject) { toast.error('Link a project before converting to BOQ'); return; }
    try {
      const res = await axios.post(`${API}/estimates/${existingEstimate.id}/convert-to-boq`, {}, { headers });
      toast.success(res.data.message);
      // Open the BOQ worksheet in a NEW TAB so the Kitchen Estimator tab stays
      // open for further edits (per user request).
      const boqUrl = res.data.boq_worksheet_id
        ? `/boq-worksheets/${res.data.boq_worksheet_id}/edit`
        : '/boq-worksheets';
      window.open(boqUrl, '_blank', 'noopener,noreferrer');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert'); }
  };

  // --- EXPORT EXCEL ---
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const summaryData = [
      ['Wardrobe Estimate - ' + estimateName], [],
      ['Dimensions', `${width}ft W x ${height}ft H x ${depth}ft D`],
      ['Type', wardrobeType], [],
      ['Material', 'Area (sqft)', 'Sheets', 'Wastage %', 'Cost'],
      ...calculations.materialSummary.map(m => [m.name, m.used.toFixed(1), m.sheets, m.wastage.toFixed(1) + '%', m.total_cost.toFixed(0)]),
      [], ['Total Project Cost', '', '', '', calculations.totalProjectCost.toFixed(0)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');
    const cutData = [
      ['Code', 'Part', 'Qty', 'Width (mm)', 'Height (mm)', 'Material', 'Laminate'],
      ...calculations.cutList.map(c => [c.code, c.part, c.qty, c.w, c.h, c.material, c.laminate]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cutData), 'Cut List');
    XLSX.writeFile(wb, `${estimateName.replace(/\s+/g, '_')}_Estimate.xlsx`);
  };

  // --- EXPORT PDF (via backend for company branding) ---
  const exportPDF = async () => {
    try {
      toast.info('Generating branded PDF...');
      const payload = {
        name: estimateName,
        project_id: linkedProject || null,
        client_id: linkedClient || null,
        dimensions: { width, height, depth },
        config: { wardrobeType, shelfCount, drawerCount, drawerWidthType, drawerHeight, includeGlassProfile, productType },
        material_summary: calculations.materialSummary,
        cut_list: calculations.cutList,
        calculations: {
          baseCost: calculations.baseCost,
          designCharges: calculations.designCharges,
          margin: calculations.margin,
          glassTotal: calculations.glassTotal,
          totalLabor: calculations.totalLabor,
          totalProjectCost: calculations.totalProjectCost,
        },
        total_cost: calculations.totalProjectCost,
        notes,
      };
      const res = await axios.post(`${API}/estimates/export-pdf`, payload, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${estimateName.replace(/\s+/g, '_')}_Estimate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF export failed');
    }
  };

  // --- GENERATE CLIENT QUOTE PDF (sales-ready, with AI render thumbnail) ---
  // --- BUILD DETAILED QUOTE PAYLOAD (shared by PDF & Email) ---
  const buildQuotePayload = () => {
    const HANDLE_LABELS = { pull_handle: 'Pull Handle', g_profile: 'G-Profile', j_profile: 'J-Profile', gola: 'Gola Profile' };
    // Modules (kitchen only) — enrich with labels + cost
    let modulesPayload = [];
    let handlesCount = 0;
    if (isKitchen && kitchen.modules) {
      const LIBS = { base: BASE_UNITS, wall: WALL_UNITS, tall: TALL_UNITS };
      kitchen.modules.forEach(m => {
        const def = (LIBS[m.category] || []).find(x => x.id === m.type);
        // iter-304c: per-module handle count — drawerCount for drawer-bearing
        // modules, else ceil(width/600) for pure shutter modules (matches storage rule)
        if (def?.hasShutter) {
          const drawers0 = Math.max(0, m.drawerCount || 0);
          const handlesPerModule = drawers0 > 0
            ? drawers0
            : Math.max(1, Math.ceil((m.widthMm || 600) / 600));
          handlesCount += handlesPerModule * (m.qty || 1);
        }
        // Rough per-module cost: carcass + shutter area × rates
        const mm2sqft = (w, h) => (w * h) / (304.8 * 304.8);
        const carcassArea = (mm2sqft(m.depthMm, m.heightMm) * 2) + (mm2sqft(m.widthMm, m.depthMm) * 2) + mm2sqft(m.widthMm, m.heightMm);
        const carcassRate = CARCASS_BY_ID[m.carcass]?.rate || 75;
        const shutterArea = def?.hasShutter ? mm2sqft(m.widthMm, m.heightMm) : 0;
        const shutterRate = SHUTTER_BY_ID[m.shutter]?.rate || 140;
        const cost = ((carcassArea * carcassRate) + (shutterArea * shutterRate)) * (m.qty || 1);
        modulesPayload.push({
          category: m.category, type: m.type,
          type_label: def?.label || m.type,
          widthMm: m.widthMm, heightMm: m.heightMm, depthMm: m.depthMm,
          qty: m.qty,
          carcass_label: CARCASS_BY_ID[m.carcass]?.label || m.carcass,
          shutter_label: def?.hasShutter ? (SHUTTER_BY_ID[m.shutter]?.label || m.shutter) : '—',
          cost,
        });
      });
    } else {
      handlesCount = calculations.shutterCount || 0;
    }

    // Countertop detailed
    let countertopPayload = null;
    if (isKitchen && kitchen.countertop?.runningFt > 0) {
      const ct = kitchen.countertop;
      const isCustom = ct.material === 'custom';
      const matLabel = isCustom ? ((ct.customName || 'Custom').trim() || 'Custom') : (COUNTERTOP_BY_ID[ct.material]?.label || '');
      const rate = isCustom ? Math.max(0, +(ct.customRate) || 0) : (COUNTERTOP_BY_ID[ct.material]?.rate || 0);
      const edge = EDGE_BY_ID[ct.edge];
      const area = ct.runningFt * (ct.depthFt || 2);
      countertopPayload = {
        material_label: matLabel, thickness_mm: ct.thicknessMm, edge_label: edge?.label,
        area_sqft: area, rate_per_sqft: rate, material_cost: area * rate,
        cutout_cost: (ct.sinkCutout ? 1500 : 0) + (ct.hobCutout ? 1200 : 0) + (ct.runningFt * (edge?.rate || 0)),
        install_labor_rate: countertopLaborRate, install_labor_cost: area * countertopLaborRate,
      };
    }
    // Backsplash detailed
    let backsplashPayload = null;
    if (isKitchen && kitchen.backsplash?.runningFt > 0) {
      const bs = kitchen.backsplash;
      const mat = BACKSPLASH_BY_ID[bs.material];
      const area = bs.runningFt * bs.heightFt;
      backsplashPayload = {
        material_label: mat?.label, area_sqft: area, rate_per_sqft: mat?.rate || 0, material_cost: area * (mat?.rate || 0),
        install_labor_rate: backsplashLaborRate, install_labor_cost: area * backsplashLaborRate,
      };
    }
    // Appliances detailed
    let appliancesPayload = [];
    if (isKitchen && kitchen.appliancesMode === 'allowance') {
      Object.entries(kitchen.appliances || {}).forEach(([key, val]) => {
        if (val > 0) appliancesPayload.push({ name: APPLIANCE_BY_ID[key]?.label || key, unit: '1', rate: val, amount: val });
      });
    }
    // Sink detailed
    let sinkItemsPayload = [];
    if (isKitchen && kitchen.sink?.type) {
      const sk = SINK_BY_ID[kitchen.sink.type];
      const fc = FAUCET_BY_ID[kitchen.sink.faucet];
      if (sk) sinkItemsPayload.push({ name: sk.label, unit: '1', rate: sk.rate, amount: sk.rate });
      if (fc) sinkItemsPayload.push({ name: fc.label, unit: '1', rate: fc.rate, amount: fc.rate });
      if (kitchen.sink.roCutout) sinkItemsPayload.push({ name: 'RO Cutout', unit: '1', rate: 400, amount: 400 });
    }
    // Plumbing (scope-based) — emit one client-facing line
    if (isKitchen && calculations?.plumbingCost > 0) {
      sinkItemsPayload.push({ name: `Plumbing Work (${kitchen.plumbing?.scope || 'standard'})`, unit: '1', rate: calculations.plumbingCost, amount: calculations.plumbingCost });
    }
    // Electrical detailed
    let electricalPayload = [];
    if (isKitchen) {
      ELECTRICAL_ITEMS.forEach(el => {
        const qty = +kitchen.electrical?.[el.id] || 0;
        if (qty > 0) electricalPayload.push({ name: el.label, unit: `${qty} ${el.unit}`, rate: el.rate, amount: qty * el.rate });
      });
    }
    // Handles (both)
    const handlesTotal = handlesCount * handleRate;

    return {
      name: estimateName,
      project_id: linkedProject || null,
      client_id: linkedClient,
      product_type: productType,
      dimensions: { width, height, depth },
      config: { wardrobeType, shelfCount, drawerCount, drawerWidthType, drawerHeight, includeGlassProfile, productType },
      material_summary: calculations.materialSummary,
      calculations: {
        baseCost: calculations.baseCost,
        designCharges: calculations.designCharges,
        margin: calculations.margin,
        glassTotal: calculations.glassTotal,
        totalProjectCost: calculations.totalProjectCost,
        shutterCount: calculations.shutterCount,
        shutterWidthMm: calculations.shutterWidthMm,
        hingesPerShutter: calculations.hingesPerShutter,
        totalHinges: calculations.totalHinges,
        totalStraighteners: calculations.totalStraighteners,
        hardwareCost: calculations.hardwareCost,
        // iter-294: surface storage-estimator material-cost components so the
        // backend can compute Module Breakdown rate (carcass + shutter +
        // laminate + edge banding + transport + labour) and emit a single
        // Module Breakdown row instead of per-sheet plywood/laminate lines.
        carcassCost: calculations.carcassCost,
        shutterCost: calculations.shutterCost,
        laminateCost: calculations.laminateCost,
        edgeBandingCost: calculations.edgeBandingCost,
        transportCost: calculations.transportCost,
        totalLabor: calculations.totalLabor,
        slidingChannelSets: calculations.slidingChannelSets,
        totalTelescopic: calculations.totalTelescopic,
        // hardware rates (so the BOQ converter can recompute hardware lines)
        hingeRate, straightenerRate, slidingChannelRate,
        telescopicRate: telescopicDrawerCost,
        // iter-296: per-estimate hardware brand selections + electrical + extras
        // so the BOQ converter and PDF can label rows with chosen brand and
        // emit dedicated Electrical / Additional Hardware sections.
        hingeBrand, hingeBrandLabel,
        straightenerBrand, straightenerBrandLabel,
        telescopicBrand, telescopicBrandLabel,
        slidingBrand, slidingBrandLabel,
        handleBrand, handleBrandLabel,
        electricalMaterial: calculations.electricalMaterial,
        electricianLabor: calculations.electricianLabor,
        extraHardwareTotal: calculations.extraHardwareTotal,
      },
      // iter-296: structured payloads consumed by backend BOQ converter + PDF
      electrical_items: !isKitchen ? electricalLines.filter(l => +l.qty > 0).map(l => ({
        id: l.id, name: l.label, qty: +l.qty, unit: l.unit,
        rate: +l.rate, amount: (+l.qty || 0) * (+l.rate || 0),
      })) : [],
      electrician_labor_pct: !isKitchen ? +electricianLaborPct : 0,
      extra_hardware_items: !isKitchen ? extraHardware.filter(l => +l.qty > 0).map(l => ({
        id: l.id, name: l.label, qty: +l.qty, unit: l.unit,
        rate: +l.rate, amount: (+l.qty || 0) * (+l.rate || 0),
      })) : [],
      modules: modulesPayload,
      countertop: countertopPayload,
      backsplash: backsplashPayload,
      appliances: appliancesPayload,
      sink_items: sinkItemsPayload,
      electrical_items: electricalPayload,
      handles: handlesCount > 0 ? { type: handleProfile, type_label: HANDLE_LABELS[handleProfile], count: handlesCount, rate: handleRate, total: handlesTotal } : null,
      // Bifurcated hardware (handles + drawer channels + tandems + trolleys — all as individual line items)
      hardware_items: (() => {
        const items = [];
        if (handlesCount > 0) items.push({ name: `Handles — ${HANDLE_LABELS[handleProfile] || handleProfile}`, unit: `${handlesCount} Nos`, rate: handleRate, amount: handlesTotal });
        if (isKitchen && kitchen?.modules) {
          // NOTE: Source of truth for drawer/trolley rates is kitchenConfig.js
          // (DRAWER_BY_ID / TROLLEY_BY_ID). Keep this in sync with the BOQ converter's
          // drawer_defaults / trolley_defaults in /app/backend/routers/estimates.py.
          const drawerUsage = {}; const trolleyUsage = {};
          kitchen.modules.forEach(m => {
            const q = +m.qty || 1;
            if (m.drawerSystem && m.drawerSystem !== 'none' && (+m.drawerCount || 0) > 0) drawerUsage[m.drawerSystem] = (drawerUsage[m.drawerSystem] || 0) + (+m.drawerCount) * q;
            if (m.trolley && m.trolley !== 'none') trolleyUsage[m.trolley] = (trolleyUsage[m.trolley] || 0) + q;
          });
          const drRates = kitchen.drawerRates || {}; const trRates = kitchen.trolleyRates || {};
          Object.entries(drawerUsage).forEach(([id, count]) => {
            const meta = DRAWER_BY_ID[id] || { label: id.replace(/_/g, ' '), rate: 500 };
            const rate = drRates[id] != null ? +drRates[id] : meta.rate;
            items.push({ name: meta.label, unit: `${count} drawers`, rate, amount: rate * count });
          });
          Object.entries(trolleyUsage).forEach(([id, count]) => {
            const meta = TROLLEY_BY_ID[id] || { label: id.replace(/_/g, ' '), rate: 1500 };
            const rate = trRates[id] != null ? +trRates[id] : meta.rate;
            items.push({ name: meta.label, unit: `${count} units`, rate, amount: rate * count });
          });
        } else if (!isKitchen) {
          // iter-294/296: Storage Estimator hardware (hinges, straighteners,
          // sliding channels, telescopic) — surfaced as bifurcated rows with
          // brand labels for client transparency.
          const brandFor = (cat, id, customLabel) => {
            const list = HARDWARE_BRANDS[cat] || [];
            const opt = list.find(b => b.id === id);
            if (!opt) return '';
            return opt.isCustom ? (customLabel || 'Custom') : opt.label;
          };
          const totalHinges = +calculations.totalHinges || 0;
          const totalStraighteners = +calculations.totalStraighteners || 0;
          const slidingChannels = +calculations.slidingChannelSets || 0;
          const totalTelescopic = drawerCount > 0 ? drawerCount : 0;
          if (totalHinges > 0) items.push({
            name: `Hinges — ${brandFor('hinge', hingeBrand, hingeBrandLabel)}`,
            unit: `${totalHinges} Nos`,
            rate: hingeRate,
            amount: totalHinges * hingeRate,
          });
          if (totalStraighteners > 0) items.push({
            name: `Door Straighteners — ${brandFor('straightener', straightenerBrand, straightenerBrandLabel)}`,
            unit: `${totalStraighteners} Nos`,
            rate: straightenerRate,
            amount: totalStraighteners * straightenerRate,
          });
          if (slidingChannels > 0) items.push({
            name: `Sliding Channel Set — ${brandFor('sliding_channel', slidingBrand, slidingBrandLabel)}`,
            unit: `${slidingChannels} Set`,
            rate: slidingChannelRate,
            amount: slidingChannels * slidingChannelRate,
          });
          if (totalTelescopic > 0) items.push({
            name: `Telescopic Drawer Slides — ${brandFor('telescopic', telescopicBrand, telescopicBrandLabel)}`,
            unit: `${totalTelescopic} Pair`,
            rate: telescopicDrawerCost,
            amount: totalTelescopic * telescopicDrawerCost,
          });
          // iter-296: Additional hardware extras — emit each non-zero row.
          extraHardware.filter(l => +l.qty > 0).forEach(l => {
            items.push({
              name: l.label,
              unit: `${+l.qty} ${l.unit || 'each'}`,
              rate: +l.rate,
              amount: (+l.qty) * (+l.rate),
            });
          });
        }
        return items;
      })(),
      overhead,
      discount,
      gst,
      terms: termsText || null,
      total_cost: finalPricing.finalClient,
      client_price_pre_gst: finalPricing.clientPreGst,
      client_base_pre_overheads: finalPricing.clientBase,
      gst_amount: finalPricing.gstAmt,
      vendor_price: finalPricing.vendor,
      admin_markup_pct: adminMarkup,
      client_markup_pct: clientMarkup,
      // Package discount auto-applied when the estimate or worksheet spans MK + MW (single-estimate: 0)
      package_discount_pct: 0,
      // Structured cost breakdown so PDF can show bifurcated Commercial Summary (Material / Hardware / Labour / Installation / Overheads)
      cost_breakdown: {
        material: Math.round(vendorBreakdown.material),
        hardware: Math.round(vendorBreakdown.hardware),
        labour: Math.round(vendorBreakdown.labour),
        installation: Math.round((countertopPayload?.install_labor_cost || 0) + (backsplashPayload?.install_labor_cost || 0)),
        // Client-side commercial overheads — freight + labour handling + consultancy (as % of Client Base, not Vendor).
        overheads: Math.round((finalPricing.freightAmt || 0) + (finalPricing.handlingAmt || 0) + (finalPricing.consultAmt || 0)),
        design: Math.round(vendorBreakdown.designCharges || 0),
      },
      render_image_base64: renderedImage || null,
      notes,
    };
  };

  // Auto-generate an AI render (Minimal Matte + Day lighting) when one doesn't exist yet.
  // Used before Client Estimate PDF / Email so the client always receives a render.
  const ensureRenderedImage = async () => {
    if (renderedImage) return renderedImage;
    try {
      toast.info('Generating AI render (minimal matte · daylight)…');
      const res = await axios.post(`${API}/render/wardrobe`, {
        width: isKitchen ? (kitchen.totalRunningFt || 10) : width,
        height: isKitchen ? (kitchen.ceilingHeight || 10) : height,
        depth: depth || 2,
        wardrobe_type: wardrobeType || 'hinged',
        laminate_finish: 'Minimal matte',
        handle_style: 'Handleless gola profile',
        lighting: 'day',
        background: 'room_scene',
        shutter_state: 'closed',
        render_mode: 'wide_room',
        product_type: productType,
        estimate_name: estimateName,
        kitchen_layout: isKitchen ? kitchen.layout : null,
        kitchen_style: isKitchen ? 'minimal_matte' : null,
        counter_material: isKitchen ? kitchen.countertop?.material : null,
        backsplash_material: isKitchen ? kitchen.backsplash?.material : null,
        appliance_set: isKitchen ? 'integrated' : null,
      }, { headers, timeout: 120000 });
      const img = res.data?.image_base64;
      if (img) {
        setRenderedImage(img);
        return img;
      }
    } catch (err) {
      toast.warning('AI render skipped (continuing without it)');
    }
    return null;
  };

  const generateClientQuote = async () => {
    if (!linkedClient) { toast.error('Link a client first to generate a client-facing estimate'); return; }
    setGeneratingQuote(true);
    try {
      // Auto-generate AI render if one hasn't been made yet (Minimal Matte + Day default)
      const img = await ensureRenderedImage();
      toast.info('Generating client-facing estimate PDF...');
      const payload = { ...buildQuotePayload(), render_image_base64: img || renderedImage || null };
      const res = await axios.post(`${API}/estimates/generate-client-quote-pdf`, payload, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${estimateName.replace(/\s+/g, '_')}_Estimate.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Client estimate downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Client estimate generation failed');
    } finally {
      setGeneratingQuote(false);
    }
  };

  // --- iter-301: 3-Tier Quote Comparison PDF ---
  const [generatingTiers, setGeneratingTiers] = useState(false);
  const generateTierComparison = async (view = 'admin') => {
    if (isKitchen) { toast.error('Tier comparison is only available for Storage estimates'); return; }
    if (!tierSummaries || tierSummaries.length < 2) { toast.error('Add dimensions and hardware first'); return; }
    setGeneratingTiers(true);
    try {
      toast.info(`Generating 3-tier ${view} comparison PDF...`);
      const payload = {
        view,
        estimate_name: estimateName,
        client_name: linkedClient?.name || '',
        project_name: linkedProject?.name || '',
        dimensions: { width: +width, height: +height, depth: +depth },
        shutter_count: +calculations.shutterCount || 0,
        drawer_count: +drawerCount || 0,
        tiers: tierSummaries.map(t => ({
          id: t.id, label: t.label, tag: t.tag, tone: t.tone,
          brands: t.brands, rates: t.rates,
          hardware_cost: Math.round(t.hardwareCost),
          vendor_cost: Math.round(t.vendor),
          client_base: Math.round(t.clientBase),
          client_pre_gst: Math.round(t.clientPreGst),
          gst_amount: Math.round(t.gstAmt),
          final_client: Math.round(t.finalClient),
          margin_pct: +t.marginPct.toFixed(1),
        })),
      };
      const res = await axios.post(`${API}/estimates/3-tier-comparison-pdf`, payload, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const viewLbl = view === 'client' ? 'Client' : view === 'vendor' ? 'Vendor' : 'Admin';
      a.download = `${estimateName.replace(/\s+/g, '_')}_3Tier_${viewLbl}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`3-Tier ${viewLbl} PDF downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || '3-tier PDF generation failed');
    } finally {
      setGeneratingTiers(false);
    }
  };

  // --- AI-GENERATE T&C ---
  const generateTermsAI = async () => {
    setGeneratingTerms(true);
    try {
      toast.info('Generating terms & conditions with AI...');
      const payload = buildQuotePayload();
      const res = await axios.post(`${API}/estimates/generate-terms`, {
        name: payload.name,
        product_type: payload.product_type,
        modules: payload.modules,
        countertop: payload.countertop,
        backsplash: payload.backsplash,
        appliances: payload.appliances,
        sink_items: payload.sink_items,
        electrical_items: payload.electrical_items,
        handles: payload.handles,
        overhead: payload.overhead,
        total_cost: payload.total_cost,
      }, { headers, timeout: 60000 });
      if (res.data?.terms) {
        setTermsText(res.data.terms);
        toast.success('Terms generated — review and edit as needed');
      } else {
        toast.error(res.data?.error || 'AI generation returned empty');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Terms generation failed');
    } finally {
      setGeneratingTerms(false);
    }
  };

  // --- SEND CLIENT QUOTE BY EMAIL ---
  const openEmailModal = () => {
    if (!linkedClient) { toast.error('Link a client first to email the quote'); return; }
    const clientObj = clients.find(c => c.id === linkedClient);
    setEmailTo(clientObj?.email || '');
    setEmailCC('');
    setEmailSubject(`Your Estimate from Bella Casa Interiors — ${estimateName}`);
    setEmailMessage('');
    setEmailModalOpen(true);
  };

  const sendQuoteEmail = async () => {
    if (!emailTo) { toast.error('Recipient email is required'); return; }
    setSendingEmail(true);
    try {
      const img = await ensureRenderedImage();
      const payload = {
        ...buildQuotePayload(),
        render_image_base64: img || renderedImage || null,
        to_email: emailTo,
        cc: emailCC || null,
        subject: emailSubject || null,
        message: emailMessage || null,
      };
      const res = await axios.post(`${API}/estimates/send-client-quote-email`, payload, { headers });
      toast.success(res.data.message || 'Estimate emailed successfully');
      setEmailModalOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Elevation diagram proportions
  const loftH = 1.6, hangingH = 3.25;
  const foldedH = Math.max(0, height - loftH - hangingH);
  const elH = 240;
  const loftPx = Math.max(0, (Math.min(loftH, height) / Math.max(height, 0.1)) * elH);
  const hangingPx = Math.max(0, (Math.min(hangingH, Math.max(0, height - loftH)) / Math.max(height, 0.1)) * elH);
  const foldedPx = Math.max(0, (foldedH / Math.max(height, 0.1)) * elH);

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="wardrobe-calculator">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2" data-testid="wardrobe-title">
            {isKitchen ? 'Kitchen Estimator' : 'Storage Estimator'}
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider font-semibold" data-testid="estimator-beta-badge">Beta</span>
          </h1>
          <p className="text-sm text-stone-500 mt-0.5">Create accurate estimates with material breakdown and cut lists</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel-btn"><FileSpreadsheet size={15} className="mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf-btn"><FileText size={15} className="mr-1" />PDF</Button>
          <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" onClick={generateClientQuote} disabled={generatingQuote || !linkedClient} data-testid="client-quote-btn" title={!linkedClient ? 'Link a client first' : 'Sales-ready client estimate with render'}>
            <FileSignature size={15} className="mr-1" />{generatingQuote ? 'Generating...' : 'Client Estimate'}
          </Button>
          <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={openEmailModal} disabled={!linkedClient} data-testid="email-quote-btn" title={!linkedClient ? 'Link a client first' : 'Email the estimate PDF to the client'}>
            <Mail size={15} className="mr-1" />Email Estimate
          </Button>
          <Button size="sm" variant="outline" onClick={() => duplicateEstimate()} data-testid="duplicate-estimate-btn" title="Create an A/B variant of this estimate">
            <Copy size={15} className="mr-1" />Duplicate
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/estimators/compare${existingEstimate ? `?a=${existingEstimate.id}` : ''}`)} data-testid="compare-variants-btn" title="Side-by-side Good/Better/Best comparison">
            <GitCompareArrows size={15} className="mr-1" />Compare
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-estimate-btn"><Save size={15} className="mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
          {existingEstimate && linkedProject && (
            <Button
              size="sm"
              onClick={handleConvertToBOQ}
              data-testid="convert-boq-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm ring-1 ring-emerald-500"
              title="Convert this estimate into a BOQ worksheet (opens in a new tab)"
            >
              <ArrowRight size={15} className="mr-1" />Convert to BOQ
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/')}
            className="border-stone-300 text-stone-700 hover:bg-stone-100"
            data-testid="close-estimator-btn"
            title="Back to Dashboard"
          >
            <X size={15} className="mr-1" />Back to Dashboard
          </Button>
        </div>
      </div>

      {/* Estimate Name + Linking */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Estimate Name</Label>
              <Input value={estimateName} onChange={e => setEstimateName(e.target.value)} data-testid="estimate-name-input" />
            </div>
            <div>
              <Label>Link to Project</Label>
              <select
                className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm"
                value={linkedProject}
                onChange={e => {
                  const newProj = e.target.value;
                  setLinkedProject(newProj);
                  // Auto-fill the linked client with the project's client so the
                  // two fields stay consistent (and so the next save doesn't show a
                  // mismatched client on the quote).
                  const proj = projects.find(p => p.id === newProj);
                  if (proj && proj.client_id) setLinkedClient(proj.client_id);
                }}
                data-testid="link-project-select"
              >
                <option value="">-- None --</option>
                {projects
                  .filter(p => p.id === linkedProject || (p.is_active !== false && ['in_progress', 'confirmed', 'active'].includes(String(p.status || '').toLowerCase())))
                  .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Link to Client</Label>
              <select
                className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm"
                value={linkedClient}
                onChange={e => setLinkedClient(e.target.value)}
                data-testid="link-client-select"
              >
                <option value="">-- None --</option>
                {(() => {
                  // When a project is linked, restrict the client dropdown to the project's client(s).
                  // Projects in our data model have a single `client_id` — so we only offer that one.
                  // If NO project is linked, show all clients as a fallback.
                  const proj = projects.find(p => p.id === linkedProject);
                  if (proj && proj.client_id) {
                    const only = clients.find(c => c.id === proj.client_id);
                    return only ? [<option key={only.id} value={only.id}>{only.name}</option>]
                      : [<option key="no-match" value="" disabled>No client linked to selected project</option>];
                  }
                  return clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>);
                })()}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Prevention Banner */}
      <ErrorPrevention
        config={{ width, height, depth, shelfCount, drawerCount, drawerHeight, includeGlassProfile }}
        calculations={calculations}
        rates={{ ply18mm, laborCost, telescopicDrawerCost, profitMargin }}
      />

      {/* Tab Navigation — colour-coded chips so each workspace is visually distinct */}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 border-b border-stone-200 pb-2" data-testid="estimator-tabs">
        {[
          { id: 'design',      label: 'Design',         icon: Ruler,      active: 'bg-blue-600 text-white ring-2 ring-blue-300',         inactive: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200' },
          { id: 'cost',        label: 'Cost Sheet',     icon: IndianRupee, active: 'bg-emerald-600 text-white ring-2 ring-emerald-300',   inactive: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200' },
          { id: 'cutlist',     label: 'Cut List',       icon: Scissors,   active: 'bg-amber-600 text-white ring-2 ring-amber-300',       inactive: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200' },
          { id: 'cutting',     label: 'Cutting Layout', icon: Scissors,   active: 'bg-orange-600 text-white ring-2 ring-orange-300',     inactive: 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200' },
          { id: 'cnc',         label: 'CNC Export',     icon: Cpu,        active: 'bg-violet-600 text-white ring-2 ring-violet-300',     inactive: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200' },
          { id: 'labels',      label: 'Labels/QR',      icon: Tag,        active: 'bg-fuchsia-600 text-white ring-2 ring-fuchsia-300',   inactive: 'bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100 border border-fuchsia-200' },
          { id: 'remnants',    label: 'Remnants',       icon: Recycle,    active: 'bg-teal-600 text-white ring-2 ring-teal-300',         inactive: 'bg-teal-50 text-teal-700 hover:bg-teal-100 border border-teal-200' },
          { id: 'templates',   label: 'Templates',      icon: Library,    active: 'bg-rose-600 text-white ring-2 ring-rose-300',         inactive: 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200' },
          { id: 'suggestions', label: 'Smart Tips',     icon: Sparkles,   active: 'bg-yellow-500 text-white ring-2 ring-yellow-300',     inactive: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200' },
          { id: 'workflow',    label: 'Production',     icon: Activity,   active: 'bg-indigo-600 text-white ring-2 ring-indigo-300',     inactive: 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200' },
          { id: 'analytics',   label: 'Analytics',      icon: BarChart3,  active: 'bg-sky-600 text-white ring-2 ring-sky-300',           inactive: 'bg-sky-50 text-sky-700 hover:bg-sky-100 border border-sky-200' },
          { id: 'render',      label: 'AI Render',      icon: Camera,     active: 'bg-pink-600 text-white ring-2 ring-pink-300',         inactive: 'bg-pink-50 text-pink-700 hover:bg-pink-100 border border-pink-200' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center justify-center gap-1.5 px-2 py-2 text-xs font-semibold whitespace-nowrap rounded-lg transition-all duration-150 ${
              activeTab === tab.id ? tab.active : tab.inactive
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Design (existing content) */}
      {activeTab === 'design' && (<>
      {/* KITCHEN BUILDER (replaces dimensions card for kitchen product type) */}
      {isKitchen && <KitchenBuilder state={kitchen} setState={setKitchen} />}
      {/* Dimensions + Config */}
      {!isKitchen && (
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-4"><Ruler size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">Dimensions & Configuration</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div><Label>{isMoulding ? 'Length (running ft)' : isPanelling ? 'Length (ft)' : isBed ? 'Width (ft)' : 'Width (ft)'}</Label><Input type="number" value={width} onChange={e => setWidth(+e.target.value)} data-testid="width-input" /></div>
            {!isMoulding && <div><Label>Height (ft)</Label><Input type="number" value={height} onChange={e => setHeight(+e.target.value)} data-testid="height-input" /></div>}
            {!isPanelling && <div><Label>{isBed ? 'Length (ft)' : 'Depth (ft)'}</Label><Input type="number" value={depth} onChange={e => setDepth(+e.target.value)} data-testid="depth-input" /></div>}
            {isMoulding && (
              <>
                <div>
                  <Label>Moulding Profile</Label>
                  <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={String(mouldingProfile)} onChange={e => {
                    const p = parseFloat(e.target.value);
                    setMouldingProfile(p);
                    const rateMap = { 1: 55, 1.5: 80, 2: 110 };
                    setMouldingRate(rateMap[p] || 80);
                  }} data-testid="moulding-profile-select">
                    <option value="1">1 inch (25mm)</option>
                    <option value="1.5">1.5 inch (38mm)</option>
                    <option value="2">2 inch (50mm)</option>
                  </select>
                </div>
                <div>
                  <Label>Rate (Rs/ft)</Label>
                  <Input type="number" value={mouldingRate} onChange={e => setMouldingRate(+e.target.value)} data-testid="moulding-rate-input" />
                </div>
              </>
            )}
            {!isPanelling && !isBed && (
              <div>
                <Label>Type</Label>
                <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={wardrobeType} onChange={e => setWardrobeType(e.target.value)} data-testid="type-select">
                  <option value="hinged">Hinged</option>
                  <option value="sliding">Sliding</option>
                </select>
              </div>
            )}
            {hasShelves && <div><Label>Shelves</Label><Input type="number" value={shelfCount} onChange={e => setShelfCount(+e.target.value)} data-testid="shelves-input" /></div>}
            {!isPanelling && <div><Label>Drawers</Label><Input type="number" value={drawerCount} onChange={e => setDrawerCount(+e.target.value)} data-testid="drawers-input" /></div>}
            {hasDrawers && (
              <>
                <div>
                  <Label>Drawer Width</Label>
                  <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={drawerWidthType} onChange={e => setDrawerWidthType(e.target.value)}>
                    <option value="half">Half Width</option>
                    <option value="full">Full Width</option>
                  </select>
                </div>
                <div>
                  <Label>Drawer Height (in)</Label>
                  <select className="h-10 w-full rounded-md border border-stone-300 px-3 text-sm" value={String(drawerHeight)} onChange={e => setDrawerHeight(+e.target.value)}>
                    <option value="6">6 inch</option>
                    <option value="8">8 inch</option>
                    <option value="10">10 inch</option>
                  </select>
                </div>
              </>
            )}
          </div>
          {!isPanelling && !isBed && (
            <div className="mt-3 flex items-center gap-2">
              <input type="checkbox" checked={includeGlassProfile} onChange={e => setIncludeGlassProfile(e.target.checked)} className="rounded" />
              <span className="text-sm text-stone-700">Include Glass Profile (+Rs {glassProfileCost})</span>
            </div>
          )}
          {isPanelling && (
            <p className="mt-2 text-xs text-blue-600 bg-blue-50 px-3 py-1.5 rounded">
              {isMoulding ? `Moulding: ${mouldingProfile}" profile × ${width} running feet @ Rs ${mouldingRate}/ft` :
               isGypsum ? "Gypsum board (6'x4' sheet) + Paint finish. Width = wall running feet." :
               'Width = wall running feet. Finish: Laminate or Paint.'}
            </p>
          )}
        </CardContent>
      </Card>
      )}

      {/* Labour & Installation (merged: Material Rates + Installation Labor) — always expanded */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <Hammer size={18} className="text-stone-600" />
            <h3 className="font-bold text-stone-800 flex-1">Labour & Installation (INR)</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {!isKitchen && <>
                <div><Label>18mm Ply (Rs/sqft)</Label><Input type="number" value={ply18mm} onChange={e => setPly18mm(+e.target.value)} /></div>
                <div><Label>12mm Ply (Rs/sqft)</Label><Input type="number" value={ply12mm} onChange={e => setPly12mm(+e.target.value)} /></div>
                <div><Label>8mm Ply (Rs/sqft)</Label><Input type="number" value={ply8mm} onChange={e => setPly8mm(+e.target.value)} /></div>
                <div><Label>HDHMR (Rs/sqft)</Label><Input type="number" value={hdhmr16mm} onChange={e => setHdhmr16mm(+e.target.value)} /></div>
                <div><Label>Int. Laminate (Rs/sheet)</Label><Input type="number" value={internalLaminateRate} onChange={e => setInternalLaminateRate(+e.target.value)} /></div>
                <div><Label>Ext. Laminate (Rs/sheet)</Label><Input type="number" value={externalLaminateRate} onChange={e => setExternalLaminateRate(+e.target.value)} /></div>
                <div><Label>Drawer Channel (Rs)</Label><Input type="number" value={telescopicDrawerCost} onChange={e => setTelescopicDrawerCost(+e.target.value)} /></div>
                <div><Label>Edge Band (Rs/m)</Label><Input type="number" value={edgeBandingRate} onChange={e => setEdgeBandingRate(+e.target.value)} /></div>
              </>}
              <div><Label>Woodworking Labour (Rs/sqft)</Label><Input type="number" value={laborCost} onChange={e => setLaborCost(+e.target.value)} data-testid="woodworking-labour" /></div>
              {isKitchen && <>
                <div><Label>Countertop Install (Rs/sqft)</Label><Input type="number" value={countertopLaborRate} onChange={e => setCountertopLaborRate(+e.target.value)} data-testid="countertop-labor-rate" /></div>
                <div><Label>Backsplash Install (Rs/sqft)</Label><Input type="number" value={backsplashLaborRate} onChange={e => setBacksplashLaborRate(+e.target.value)} data-testid="backsplash-labor-rate" /></div>
              </>}
              <div><Label>Design Charge (%)</Label><Input type="number" value={designChargePercent} onChange={e => setDesignChargePercent(+e.target.value)} /></div>
              <div><Label>Profit Margin (%)</Label><Input type="number" value={profitMargin} onChange={e => setProfitMargin(+e.target.value)} /></div>
              {hasShutters && wardrobeType === 'hinged' && (
                <div><Label>Hinge Rate (Rs/unit)</Label><Input type="number" value={hingeRate} onChange={e => setHingeRate(+e.target.value)} data-testid="hinge-rate-input" /></div>
              )}
              {hasShutters && wardrobeType === 'sliding' && (
                <div><Label>Sliding Channel (Rs/set)</Label><Input type="number" value={slidingChannelRate} onChange={e => setSlidingChannelRate(+e.target.value)} data-testid="sliding-channel-rate-input" /></div>
              )}
              {hasShutters && (
                <div><Label>Door Straightener (Rs/unit)</Label><Input type="number" value={straightenerRate} onChange={e => setStraightenerRate(+e.target.value)} data-testid="straightener-rate-input" /></div>
              )}
            </div>
        </CardContent>
      </Card>

      {/* Results + Material Summary + Cut List moved to bottom (just above Terms & Conditions) */}

      {/* iter-296: Hardware Brands & Rates / Electrical / Extras
          iter-304c (P4): now also visible for Kitchen — handle/hinge/lift-stay
          rates flow into kitchen.hardware_overrides so the Material Summary
          + Estimator → BOQ converter both pick them up in real time. */}
      {!isMoulding && !isPanelling && !isGypsum && !isBed && (
        <Card data-testid="storage-hardware-extras-card">
          <CardContent className="pt-5 pb-4 space-y-5">
            {/* iter-300: Brand Portfolio Switcher — one-click tier presets */}
            {/* iter-301: + Compare 3 Tiers PDF + live mini-totals per tile */}
            <BrandPortfolioSwitcher
              tierSummaries={tierSummaries}
              comparing={generatingTiers}
              onCompareTiers={generateTierComparison}
              onApply={(preset) => {
                const r = preset.resolved || {};
                if (r.hinge) { setHingeBrand(r.hinge.id); setHingeRate(r.hinge.rate); setHingeBrandLabel(r.hinge.label); }
                if (r.straightener) { setStraightenerBrand(r.straightener.id); setStraightenerRate(r.straightener.rate); setStraightenerBrandLabel(r.straightener.label); }
                if (r.telescopic) { setTelescopicBrand(r.telescopic.id); setTelescopicDrawerCost(r.telescopic.rate); setTelescopicBrandLabel(r.telescopic.label); }
                if (r.sliding_channel) { setSlidingBrand(r.sliding_channel.id); setSlidingChannelRate(r.sliding_channel.rate); setSlidingBrandLabel(r.sliding_channel.label); }
                if (r.handle) { setHandleBrand(r.handle.id); setHandleRate(r.handle.rate); setHandleBrandLabel(r.handle.label); }
                // P4: also patch kitchen overrides so brand portfolio drives kitchen pricing
                if (isKitchen) {
                  setKitchen(prev => ({
                    ...prev,
                    hardware_overrides: {
                      ...(prev.hardware_overrides || {}),
                      ...(r.hinge ? { hingeRate: r.hinge.rate, hingeBrand: r.hinge.label } : {}),
                      ...(r.handle ? { handleRate: r.handle.rate, handleBrand: r.handle.label } : {}),
                      ...(r.telescopic ? { telescopicRate: r.telescopic.rate } : {}),
                    },
                  }));
                }
                toast.success(`Applied ${preset.label} brand portfolio`);
              }}
            />
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-bold text-stone-900 text-base">Hardware Brands &amp; Rates</h3>
              <span className="text-[11px] text-stone-500">Pick a preset brand or override the rate per estimate.</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BrandRateRow
                label="Hinges"
                category="hinge"
                brand={hingeBrand} onBrandChange={setHingeBrand}
                customLabel={hingeBrandLabel} onCustomLabelChange={setHingeBrandLabel}
                rate={hingeRate} onRateChange={setHingeRate}
                rateOnBrandChange={setHingeRate}
                testId="brand-hinge"
              />
              <BrandRateRow
                label="Door Straighteners"
                category="straightener"
                brand={straightenerBrand} onBrandChange={setStraightenerBrand}
                customLabel={straightenerBrandLabel} onCustomLabelChange={setStraightenerBrandLabel}
                rate={straightenerRate} onRateChange={setStraightenerRate}
                rateOnBrandChange={setStraightenerRate}
                testId="brand-straightener"
              />
              <BrandRateRow
                label="Telescopic Slides"
                category="telescopic"
                brand={telescopicBrand} onBrandChange={setTelescopicBrand}
                customLabel={telescopicBrandLabel} onCustomLabelChange={setTelescopicBrandLabel}
                rate={telescopicDrawerCost} onRateChange={setTelescopicDrawerCost}
                rateOnBrandChange={setTelescopicDrawerCost}
                testId="brand-telescopic"
              />
              <BrandRateRow
                label="Sliding Channel Set"
                category="sliding_channel"
                brand={slidingBrand} onBrandChange={setSlidingBrand}
                customLabel={slidingBrandLabel} onCustomLabelChange={setSlidingBrandLabel}
                rate={slidingChannelRate} onRateChange={setSlidingChannelRate}
                rateOnBrandChange={setSlidingChannelRate}
                testId="brand-sliding"
              />
              <BrandRateRow
                label="Handles / Profiles"
                category="handle"
                brand={handleBrand} onBrandChange={setHandleBrand}
                customLabel={handleBrandLabel} onCustomLabelChange={setHandleBrandLabel}
                rate={handleRate} onRateChange={setHandleRate}
                rateOnBrandChange={setHandleRate}
                testId="brand-handle"
              />
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-stone-900 text-base">Electrical &amp; Lighting</h3>
                <div className="flex items-center gap-2 text-xs">
                  <Label className="text-stone-600">Electrician labour</Label>
                  <Input type="number" value={electricianLaborPct} onChange={(e) => setElectricianLaborPct(+e.target.value)} className="w-16 h-7" data-testid="electrician-labor-pct" />
                  <span className="text-stone-500">% of material</span>
                </div>
              </div>
              <QtyRateGrid
                lines={electricalLines}
                onChange={setElectricalLines}
                testIdPrefix="electrical"
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-bold text-stone-900 text-base mb-2">Additional Hardware</h3>
              <QtyRateGrid
                lines={extraHardware}
                onChange={setExtraHardware}
                testIdPrefix="extra-hw"
              />
            </div>

            {/* Live total preview */}
            <div className="text-xs bg-stone-50 border rounded-md px-3 py-2 flex items-center justify-between" data-testid="storage-extras-summary">
              <span className="text-stone-600">Electrical material + labour: <b className="text-stone-900">Rs {Math.round((calculations.electricalMaterial || 0) + (calculations.electricianLabor || 0)).toLocaleString()}</b></span>
              <span className="text-stone-600">Additional hardware: <b className="text-stone-900">Rs {Math.round(calculations.extraHardwareTotal || 0).toLocaleString()}</b></span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notes */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <Label>Notes</Label>
          <textarea className="w-full mt-1 h-20 rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional notes for this estimate..." />
        </CardContent>
      </Card>

      {/* Handle Profile (applies to wardrobe + kitchen shutters) */}
      {hasShutters || isKitchen ? (
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-3"><Hammer size={18} className="text-stone-600" /><h3 className="font-bold text-stone-800">Handle Profile</h3></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Profile Type</Label>
                <select value={handleProfile} onChange={e => setHandleProfile(e.target.value)} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="handle-profile-select">
                  <option value="pull_handle">Pull Handle</option>
                  <option value="gola">Gola Profile (handleless)</option>
                  <option value="g_profile">G-Profile</option>
                  <option value="j_profile">J-Profile</option>
                </select>
              </div>
              <div><Label>Rate (Rs/unit)</Label><Input type="number" value={handleRate} onChange={e => setHandleRate(+e.target.value)} data-testid="handle-rate-input" /></div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Installation Labor merged into Labour & Installation card above. */}

      {/* Commercial Overheads/Discount/GST moved to Cost Sheet tab for real-time linkage. */}

      {/* === Cost Summary (moved to bottom) === */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-stone-900 text-white border-0">
          <CardContent className="pt-6 pb-5">
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={18} className="text-emerald-400" />
              <span className="text-stone-400 text-xs uppercase font-bold tracking-wide">Estimated Project Cost (Vendor / Raw)</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="ml-auto text-stone-400 hover:text-emerald-300 transition-colors"
                    title="See where this number comes from"
                    data-testid="cost-tile-breakdown-trigger"
                  >
                    <Info size={14} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  side="bottom"
                  sideOffset={8}
                  className="w-80 p-0 border-stone-200 shadow-xl z-50"
                  data-testid="cost-tile-breakdown-popover"
                >
                  <div className="px-4 py-3 border-b bg-stone-50">
                    <p className="text-xs font-bold uppercase tracking-wider text-stone-700">Where this Rs {Math.round(vendorProjectCost).toLocaleString('en-IN')} comes from</p>
                    <p className="text-[10px] text-stone-500 mt-0.5">Vendor / Raw — pre-markup, pre-overheads</p>
                  </div>
                  {(() => {
                    const total = Math.max(vendorProjectCost, 1);
                    const matRows = calculations.materialSummary || [];
                    const HARDWARE_NAME_RE = /(hinge|handle|straightener|sliding channel|drawer slide|tandem|trolley|corner system|lift-?up|stay)/i;
                    const baseMaterial = matRows.filter(m => !HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
                    const baseHardware = matRows.filter(m => HARDWARE_NAME_RE.test(m.name || '')).reduce((a, m) => a + (+m.total_cost || 0), 0);
                    const elecMat = +(calculations.electricalMaterial || 0);
                    const elecLab = +(calculations.electricianLabor || 0);
                    const extras = +(calculations.extraHardwareTotal || 0);
                    const baseLabour = +(calculations.totalLabor || 0);
                    const ctInstall = isKitchen && kitchen.countertop?.runningFt > 0 ? (kitchen.countertop.runningFt * (kitchen.countertop.depthFt || 2) * countertopLaborRate) : 0;
                    const bsInstall = isKitchen && kitchen.backsplash?.runningFt > 0 ? (kitchen.backsplash.runningFt * kitchen.backsplash.heightFt * backsplashLaborRate) : 0;
                    const labour = baseLabour + ctInstall + bsInstall + elecLab;
                    const designC = +(calculations.designCharges || 0);
                    const rows = [
                      { label: 'Carcass + Shutter + Laminate', val: baseMaterial, color: 'bg-emerald-500' },
                      { label: 'Hinges, Slides, Channels', val: baseHardware, color: 'bg-blue-500' },
                      { label: 'Electrical (LEDs, drivers, sensors)', val: elecMat, color: 'bg-amber-500' },
                      { label: 'Additional Hardware (locks, gas-lift)', val: extras, color: 'bg-purple-500' },
                      { label: 'Labour & Installation', val: labour, color: 'bg-rose-500' },
                      { label: `Design Charges (${designChargePercent}%)`, val: designC, color: 'bg-stone-500' },
                    ].filter(r => r.val > 0);
                    return (
                      <div className="px-4 py-3 space-y-2.5" data-testid="cost-tile-breakdown-rows">
                        {rows.map((r, i) => {
                          const pct = (r.val / total) * 100;
                          return (
                            <div key={i} data-testid={`cost-row-${i}`}>
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="text-stone-700 truncate pr-2" title={r.label}>{r.label}</span>
                                <span className="font-mono font-semibold text-stone-900 whitespace-nowrap">
                                  Rs {Math.round(r.val).toLocaleString('en-IN')}
                                  <span className="ml-1.5 text-stone-400 font-normal">{pct.toFixed(1)}%</span>
                                </span>
                              </div>
                              <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                <div className={`h-full ${r.color} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                            </div>
                          );
                        })}
                        <div className="pt-2 mt-2 border-t flex items-center justify-between text-xs">
                          <span className="font-bold text-stone-900">Vendor Total</span>
                          <span className="font-mono font-bold text-emerald-700">Rs {Math.round(vendorProjectCost).toLocaleString('en-IN')}</span>
                        </div>
                        <p className="text-[10px] text-stone-400 italic pt-1">Profit margin & client/admin markups apply on top in the Cost Sheet tab.</p>
                      </div>
                    );
                  })()}
                </PopoverContent>
              </Popover>
              {/* iter-299: Live BOQ Preview — see exactly what the BOQ will
                  contain before converting. Storage only (kitchen preview
                  pending). */}
              {!isKitchen && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button
                      type="button"
                      className="text-stone-400 hover:text-emerald-300 transition-colors"
                      title="Preview BOQ line items before converting"
                      data-testid="live-boq-preview-trigger"
                    >
                      <Eye size={14} />
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="live-boq-preview-drawer">
                    <SheetHeader>
                      <SheetTitle className="flex items-center gap-2">
                        <Eye size={18} className="text-emerald-600" />
                        Live BOQ Preview
                      </SheetTitle>
                      <p className="text-xs text-stone-500">
                        Exactly what the client will see after <b>Convert to BOQ</b> — same rates, same line items, no surprises.
                      </p>
                    </SheetHeader>
                    <div className="mt-5 space-y-4">
                      {(() => {
                        const sections = {};
                        boqPreviewItems.forEach(it => {
                          if (!sections[it.section]) sections[it.section] = [];
                          sections[it.section].push(it);
                        });
                        const entries = Object.entries(sections);
                        if (entries.length === 0) {
                          return <p className="text-sm text-stone-500 italic text-center py-8" data-testid="live-boq-preview-empty">Add dimensions and hardware to see BOQ lines.</p>;
                        }
                        let grand = 0;
                        return (
                          <>
                            {entries.map(([section, items]) => {
                              const secTotal = items.reduce((s, i) => s + (i.qty * i.clientRate), 0);
                              grand += secTotal;
                              return (
                                <div key={section} className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`live-boq-section-${section.replace(/\s+/g, '-').toLowerCase()}`}>
                                  <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
                                    <p className="text-xs font-bold uppercase tracking-wider text-amber-900">{section}</p>
                                  </div>
                                  <table className="w-full text-xs">
                                    <thead className="bg-stone-50 text-stone-600">
                                      <tr>
                                        <th className="text-left px-3 py-1.5 font-semibold">Item</th>
                                        <th className="text-right px-2 py-1.5 font-semibold">Qty</th>
                                        <th className="text-left px-2 py-1.5 font-semibold">Unit</th>
                                        <th className="text-right px-2 py-1.5 font-semibold">Rate</th>
                                        <th className="text-right px-3 py-1.5 font-semibold">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {items.map((it, idx) => (
                                        <tr key={idx} className="border-t border-stone-100" data-testid={`live-boq-row-${idx}`}>
                                          <td className="px-3 py-2 text-stone-800">{it.name}</td>
                                          <td className="px-2 py-2 text-right font-mono text-stone-700">{it.qty}</td>
                                          <td className="px-2 py-2 text-stone-600">{it.unit}</td>
                                          <td className="px-2 py-2 text-right font-mono text-stone-700">Rs {Math.round(it.clientRate).toLocaleString('en-IN')}</td>
                                          <td className="px-3 py-2 text-right font-mono font-semibold text-stone-900">Rs {Math.round(it.qty * it.clientRate).toLocaleString('en-IN')}</td>
                                        </tr>
                                      ))}
                                      <tr className="border-t border-emerald-200 bg-emerald-50">
                                        <td colSpan={4} className="px-3 py-1.5 text-right text-xs font-bold text-emerald-800">Subtotal — {section}</td>
                                        <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-800">Rs {Math.round(secTotal).toLocaleString('en-IN')}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              );
                            })}
                            <div className="flex items-center justify-between bg-stone-900 text-white rounded-lg px-4 py-3 mt-4" data-testid="live-boq-preview-grand-total">
                              <span className="text-xs uppercase tracking-wide font-semibold text-stone-300">Client Basic Total (pre-GST, pre-overheads)</span>
                              <span className="text-lg font-bold font-mono">Rs {Math.round(grand).toLocaleString('en-IN')}</span>
                            </div>
                            <p className="text-[10px] text-stone-400 italic mt-2">* Discount, freight, labour handling & GST apply in the Cost Sheet tab.</p>
                          </>
                        );
                      })()}
                    </div>
                  </SheetContent>
                </Sheet>
              )}
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold" data-testid="total-cost">Rs {Math.round(vendorProjectCost).toLocaleString('en-IN')}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
              <div><span className="text-stone-400">Material</span><p className="font-semibold">Rs {Math.round(vendorBreakdown.material).toLocaleString('en-IN')}</p></div>
              <div><span className="text-stone-400">Hardware</span><p className="font-semibold">Rs {Math.round(vendorBreakdown.hardware).toLocaleString('en-IN')}</p></div>
              <div><span className="text-stone-400">Labour</span><p className="font-semibold">Rs {Math.round(vendorBreakdown.labour).toLocaleString('en-IN')}</p></div>
              <div><span className="text-stone-400">{isKitchen ? 'Design & Margin' : `Design (${designChargePercent}%)`}</span><p className="font-semibold">Rs {Math.round(vendorBreakdown.designCharges || 0).toLocaleString('en-IN')}</p></div>
            </div>
            <p className="text-[11px] text-stone-400 mt-3">This is the raw / vendor price — Cost Sheet tab adds Admin + Client markups, discounts & GST.</p>
          </CardContent>
        </Card>
        {!isKitchen && (
        <Card>
          <CardContent className="pt-5 flex flex-col items-center">
            <span className="text-xs font-bold text-stone-500 uppercase mb-2">Internal Elevation</span>
            <svg width="180" height={elH} className="border border-stone-200 rounded">
              <rect x="5" y="5" width="170" height={Math.max(0, loftPx - 2)} fill="#f1f5f9" stroke="#64748b" strokeWidth="1.5" />
              <text x="90" y={loftPx / 2 + 4} fontSize="10" fill="#475569" textAnchor="middle">Loft</text>
              <rect x="5" y={loftPx + 3} width="170" height={Math.max(0, hangingPx - 2)} fill="#fefce8" stroke="#64748b" strokeWidth="1.5" />
              <text x="90" y={loftPx + hangingPx / 2 + 4} fontSize="10" fill="#475569" textAnchor="middle">Hanging</text>
              <rect x="5" y={loftPx + hangingPx + 3} width="170" height={Math.max(0, foldedPx - 8)} fill="#f0fdf4" stroke="#64748b" strokeWidth="1.5" />
              <text x="90" y={loftPx + hangingPx + foldedPx / 2} fontSize="10" fill="#475569" textAnchor="middle">Shelves / Drawers</text>
              <text x="90" y={elH - 2} fontSize="9" fill="#94a3b8" textAnchor="middle">{width}ft W x {height}ft H</text>
            </svg>
          </CardContent>
        </Card>
        )}
        {isKitchen && (
        <Card>
          <CardContent className="pt-5 flex flex-col items-center">
            <span className="text-xs font-bold text-stone-500 uppercase mb-2">Kitchen Snapshot</span>
            <div className="text-center space-y-1.5 py-4">
              <p className="text-5xl">🍳</p>
              <p className="text-xs text-stone-500">{KITCHEN_LAYOUTS_LABELS[kitchen.layout] || kitchen.layout}</p>
              <p className="text-xs font-semibold text-stone-700">{kitchen.modules.length} modules</p>
              <p className="text-xs text-stone-500">Countertop: {kitchen.countertop.runningFt}ft {(kitchen.countertop.material || '').toUpperCase()}</p>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* === Material Summary (split for Kitchen: Material / Hardware / Labour / Design) === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Package size={18} className="text-purple-600" /><h3 className="font-bold text-stone-800">Material Summary</h3></div>
          {isKitchen ? (() => {
            // iter-304d (P2): 4 separate tables tagged by `kind`
            const groups = [
              { kind: 'material', label: 'Material', tint: 'bg-blue-50 border-blue-200', accent: 'text-blue-700' },
              { kind: 'hardware', label: 'Hardware', tint: 'bg-amber-50 border-amber-200', accent: 'text-amber-700' },
              { kind: 'labour', label: 'Labour & Installation', tint: 'bg-emerald-50 border-emerald-200', accent: 'text-emerald-700' },
              { kind: 'design', label: 'Design & Margin', tint: 'bg-purple-50 border-purple-200', accent: 'text-purple-700' },
            ];
            const fmt = (n) => `Rs ${(+n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
            const sectionRows = (kind) => calculations.materialSummary.filter(m => (m.kind || 'material') === kind);
            return (
              <div className="space-y-5">
                {groups.map(g => {
                  const rows = sectionRows(g.kind);
                  if (!rows.length) return null;
                  const subtotal = rows.reduce((a, m) => a + (+m.total_cost || 0), 0);
                  return (
                    <div key={g.kind} className={`rounded-lg border ${g.tint} overflow-hidden`} data-testid={`mat-summary-section-${g.kind}`}>
                      <div className={`flex items-center justify-between px-3 py-2 ${g.accent} font-bold tracking-wider text-xs uppercase border-b ${g.tint}`}>
                        <span>{g.label}</span>
                        <span className="text-sm font-mono">{fmt(subtotal)}</span>
                      </div>
                      <div className="overflow-x-auto bg-white">
                        <table className="w-full text-sm" data-testid={`mat-summary-table-${g.kind}`}>
                          <thead>
                            <tr className="bg-stone-50 border-b">
                              <th className="px-3 py-2 text-left font-semibold">Item</th>
                              <th className="px-3 py-2 text-right font-semibold">Qty / Area</th>
                              <th className="px-3 py-2 text-right font-semibold">Unit</th>
                              <th className="px-3 py-2 text-right font-semibold">Rate</th>
                              <th className="px-3 py-2 text-right font-semibold">Wastage</th>
                              <th className="px-3 py-2 text-right font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((m, i) => (
                              <tr key={i} className="border-b last:border-b-0">
                                <td className="px-3 py-2">{m.name}</td>
                                <td className="px-3 py-2 text-right">{(+(m.used) || 0).toFixed(1)}</td>
                                <td className="px-3 py-2 text-right text-stone-500">{m.unit || 'sqft'}</td>
                                <td className="px-3 py-2 text-right font-mono">{fmt(m.unit_rate)}</td>
                                <td className={`px-3 py-2 text-right ${m.wastage > 20 ? 'text-red-600 font-semibold' : 'text-stone-500'}`}>{(+m.wastage || 0).toFixed(1)}%</td>
                                <td className="px-3 py-2 text-right font-medium">{fmt(m.total_cost)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
                <div className="rounded-lg bg-stone-900 text-white p-3 flex items-center justify-between" data-testid="material-subtotal-row">
                  <span className="font-bold tracking-wider text-xs">GRAND SUBTOTAL (All sections)</span>
                  <span className="font-bold text-lg font-mono">{fmt(calculations.materialSummary.reduce((a, m) => a + (+m.total_cost || 0), 0))}</span>
                </div>
              </div>
            );
          })() : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="material-summary-table">
                <thead><tr className="bg-stone-50 border-b"><th className="px-3 py-2 text-left font-semibold">Material</th><th className="px-3 py-2 text-right font-semibold">Area (sqft)</th><th className="px-3 py-2 text-right font-semibold">Sheets</th><th className="px-3 py-2 text-right font-semibold">Wastage</th><th className="px-3 py-2 text-right font-semibold">Cost</th></tr></thead>
                <tbody>
                  {calculations.materialSummary.map((m, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-3 py-2">{m.name}</td>
                      <td className="px-3 py-2 text-right">{m.used.toFixed(1)}</td>
                      <td className="px-3 py-2 text-right">{m.sheets}</td>
                      <td className={`px-3 py-2 text-right ${m.wastage > 20 ? 'text-red-600 font-semibold' : ''}`}>{m.wastage.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right font-medium">Rs {m.total_cost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                  <tr className="bg-stone-900 text-white font-bold" data-testid="material-subtotal-row">
                    <td className="px-3 py-2.5" colSpan={4}>SUBTOTAL (Material + Hardware)</td>
                    <td className="px-3 py-2.5 text-right">Rs {calculations.materialSummary.reduce((a, m) => a + (+m.total_cost || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-stone-600">
            {isKitchen ? (<>
              <span>Modules: {kitchen.modules.length}</span>
              <span>Layout: {kitchen.layout.replace('_', '-').toUpperCase()}</span>
              <span>Hinges: {calculations.totalHinges || 0}</span>
              <span>Handles: {calculations.totalHandles || 0}</span>
              <span>Drawer Slides: {calculations.totalDrawerSlides || 0}</span>
              <span>Countertop: {kitchen.countertop.runningFt}ft</span>
              <span>Backsplash: {kitchen.backsplash.runningFt}ft</span>
              <span>Running ft: {kitchen.totalRunningFt}</span>
            </>) : (<>
              <span>Shutters: {calculations.shutterCount || 0} × {calculations.shutterWidthMm ? Math.round(calculations.shutterWidthMm) + 'mm' : '—'}</span>
              <span>Hinges: {calculations.totalHinges} ({calculations.hingesPerShutter || 0}/shutter)</span>
              <span>Drawer Channels: {calculations.drawerCount}</span>
              <span>Straighteners: {calculations.totalStraighteners || 0}</span>
              <span>Adhesive: {calculations.adhesiveKg} kg</span>
              <span>Edge Band: {calculations.totalEdgeBandingMeters.toFixed(1)} m</span>
              {calculations.slidingChannelSets > 0 && <span>Sliding Track: {calculations.slidingChannelSets}m</span>}
            </>)}
          </div>
          {isKitchen && calculations.warnings?.length > 0 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="kitchen-warnings">
              <div className="flex items-center gap-2 mb-2"><Sparkles size={14} className="text-amber-600" /><span className="text-sm font-semibold text-amber-800">Smart Kitchen Warnings</span></div>
              <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                {calculations.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === Cut List (moved to bottom) === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Hammer size={18} className="text-orange-600" /><h3 className="font-bold text-stone-800">Detailed Cut List (Carpenter Codes)</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cut-list-table">
              <thead><tr className="bg-stone-50 border-b"><th className="px-2 py-2 text-left font-semibold">Code</th><th className="px-2 py-2 text-left font-semibold">Part</th><th className="px-2 py-2 text-center font-semibold">Qty</th><th className="px-2 py-2 text-right font-semibold">W (mm)</th><th className="px-2 py-2 text-right font-semibold">H (mm)</th><th className="px-2 py-2 text-left font-semibold">Material</th><th className="px-2 py-2 text-left font-semibold">Laminate</th></tr></thead>
              <tbody>
                {calculations.cutList.map((c, i) => (
                  <tr key={i} className="border-b hover:bg-stone-50">
                    <td className="px-2 py-2 font-mono font-bold text-blue-700">{c.code}</td>
                    <td className="px-2 py-2">{c.part}</td>
                    <td className="px-2 py-2 text-center">{c.qty}</td>
                    <td className="px-2 py-2 text-right">{c.w}</td>
                    <td className="px-2 py-2 text-right">{c.h}</td>
                    <td className="px-2 py-2">{c.material}</td>
                    <td className="px-2 py-2">{c.laminate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-stone-500 mt-2">Codes help carpenters identify each panel during cutting, edge banding, lamination and assembly.</p>
        </CardContent>
      </Card>

      {/* Terms & Conditions with AI */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><FileText size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">Terms & Conditions</h3>
              <span className="text-xs text-stone-500">(appears in Client Estimate PDF)</span></div>
            <Button variant="outline" size="sm" onClick={generateTermsAI} disabled={generatingTerms} data-testid="generate-terms-btn">
              <Sparkles size={14} className="mr-1" />{generatingTerms ? 'Generating...' : 'Generate with AI'}
            </Button>
          </div>
          <textarea
            className="w-full h-40 rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            value={termsText}
            onChange={e => setTermsText(e.target.value)}
            placeholder="Add terms & conditions for this estimate. Click 'Generate with AI' to auto-create based on included items, overheads and payment schedule..."
            data-testid="terms-text"
          />
          <p className="text-xs text-stone-500 mt-2">These terms will be included in the published estimate PDF and email.</p>
        </CardContent>
      </Card>
      </>)}

      {/* TAB: Cost Sheet */}
      {activeTab === 'cost' && (
        <CostSheetEngine
          calculations={calculations}
          rates={{ glassProfileCost, profitMargin, ...calculations }}
          config={{ width, height, depth, includeGlassProfile }}
          overhead={overhead}
          setOverhead={setOverhead}
          discount={discount}
          setDiscount={setDiscount}
          gstConfig={gst}
          setGstConfig={setGst}
          installLabor={{
            countertop: isKitchen && kitchen.countertop?.runningFt > 0 ? (kitchen.countertop.runningFt * (kitchen.countertop.depthFt || 2) * countertopLaborRate) : 0,
            backsplash: isKitchen && kitchen.backsplash?.runningFt > 0 ? (kitchen.backsplash.runningFt * kitchen.backsplash.heightFt * backsplashLaborRate) : 0,
          }}
          onApplySuggestion={applySuggestion}
          appliedSuggestions={appliedSuggestions}
          estimatedProjectCost={calculations.totalProjectCost}
          designChargePercent={designChargePercent}
          isKitchen={isKitchen}
          adminMarkup={adminMarkup} setAdminMarkup={setAdminMarkup}
          clientMarkup={clientMarkup} setClientMarkup={setClientMarkup}
          minMarginAlert={minMarginAlert} setMinMarginAlert={setMinMarginAlert}
          extraOverheadPct={extraOverheadPct} setExtraOverheadPct={setExtraOverheadPct}
        />
      )}

      {/* TAB: Advanced Cut List */}
      {activeTab === 'cutlist' && (
        <AdvancedCutList
          cutList={calculations.cutList}
          estimateName={estimateName}
          customOrder={cutListOrder}
          onCustomOrderChange={setCutListOrder}
        />
      )}

      {/* iter-290: Apply Cut-List custom order to downstream tabs (Cutting Layout + CNC) */}
      {(() => {
        const baseList = calculations.cutList || [];
        const orderedCutList = cutListOrder.length ? (() => {
          const rank = new Map(cutListOrder.map((code, i) => [code, i]));
          return [...baseList].sort((a, b) => {
            const ra = rank.has(a.code) ? rank.get(a.code) : 1e9;
            const rb = rank.has(b.code) ? rank.get(b.code) : 1e9;
            return ra - rb;
          });
        })() : baseList;
        return (
          <>
            {/* TAB: Cutting Layout */}
            {activeTab === 'cutting' && (
              <CuttingLayoutEngine cutList={orderedCutList} calculations={calculations} />
            )}

            {/* TAB: CNC Export */}
            {activeTab === 'cnc' && (
              <CNCExportEngine cutList={orderedCutList} config={{ width, height, depth, shelfCount, drawerCount }} />
            )}
          </>
        );
      })()}

      {/* TAB: AI Render Engine */}
      {activeTab === 'render' && (
        <RenderEngine
          config={{ width, height, depth, wardrobeType }}
          estimateName={estimateName}
          productType={productType}
          kitchenConfig={isKitchen ? kitchen : {}}
          renderedImage={renderedImage}
          onRenderUpdate={setRenderedImage}
        />
      )}

      {/* TAB: CNC Export — handled inside the iter-290 ordered-cutList block above */}

      {/* TAB: Remnant Manager */}
      {activeTab === 'remnants' && (
        <RemnantManager calculations={calculations} />
      )}

      {/* TAB: Labels / QR */}
      {activeTab === 'labels' && (
        <LabelQRSystem cutList={calculations.cutList} estimateName={estimateName} config={{ width, height, depth, clientName: linkedClient }} />
      )}

      {/* TAB: Template Library */}
      {activeTab === 'templates' && (
        <TemplateLibrary
          onApplyTemplate={(cfg, templateName, meta) => {
            setWidth(cfg.width || 4); setHeight(cfg.height || 8); setDepth(cfg.depth || 2);
            setWardrobeType(cfg.wardrobeType || 'hinged'); setShelfCount(cfg.shelfCount ?? 3);
            setDrawerCount(cfg.drawerCount ?? 2); setDrawerWidthType(cfg.drawerWidthType || 'half');
            setDrawerHeight(cfg.drawerHeight ?? 8); setIncludeGlassProfile(cfg.includeGlassProfile || false);
            if (templateName) setEstimateName(templateName);
            // Apply explicit product type from template (overrides name detection)
            setExplicitProductType(meta?.productType || null);
            if (meta?.mouldingProfile) {
              setMouldingProfile(meta.mouldingProfile);
              const rateMap = { 1: 55, 1.5: 80, 2: 110 };
              setMouldingRate(rateMap[meta.mouldingProfile] || 80);
            }
            // Apply kitchen preset (full kitchen state replacement)
            if (meta?.kitchenPreset) {
              setKitchen({ ...DEFAULT_KITCHEN_STATE, ...meta.kitchenPreset });
            }
            setActiveTab('design');
          }}
          currentConfig={{ width, height, depth, wardrobeType, shelfCount, drawerCount, drawerWidthType, drawerHeight, includeGlassProfile }}
        />
      )}

      {/* TAB: Smart Suggestions */}
      {activeTab === 'suggestions' && (
        <SmartSuggestions
          config={{ width, height, depth, shelfCount, drawerCount }}
          calculations={calculations}
          rates={{ ply18mm, laborCost, telescopicDrawerCost, profitMargin }}
        />
      )}

      {/* TAB: Production Workflow */}
      {activeTab === 'workflow' && (
        <ProductionWorkflow estimateId={existingEstimate?.id || 'new'} estimateName={estimateName} />
      )}

      {/* TAB: Analytics Dashboard */}
      {activeTab === 'analytics' && (
        <AnalyticsDashboard />
      )}

      {/* Version info */}
      {existingEstimate && (
        <p className="text-xs text-stone-400 text-center">Version {existingEstimate.version || 1} | Last saved: {existingEstimate.updated_at ? new Date(existingEstimate.updated_at).toLocaleString() : 'Never'}</p>
      )}

      {/* Email Quote Modal — matches Projects-tab email composer */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !sendingEmail && setEmailModalOpen(false)} data-testid="email-quote-modal">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[92vh] overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-bold text-stone-900 flex items-center gap-2"><Mail size={18} className="text-blue-600" />Send Client Estimate</h3>
                <p className="text-xs text-stone-500 mt-0.5">Branded PDF will be attached. AI render auto-generated if none exists (minimal matte · daylight).</p>
              </div>
              <button onClick={() => setEmailModalOpen(false)} className="text-stone-400 hover:text-stone-700" disabled={sendingEmail}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-0 flex-1 overflow-hidden">
              {/* Composer */}
              <div className="col-span-2 p-5 space-y-3 overflow-y-auto">
                <div><Label>To *</Label><Input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="client@example.com" data-testid="email-to-input" /></div>
                <div><Label>CC (optional, comma-separated)</Label><Input type="text" value={emailCC} onChange={e => setEmailCC(e.target.value)} placeholder="manager@bellacasa.com" data-testid="email-cc-input" /></div>
                <div><Label>Subject</Label><Input type="text" value={emailSubject} onChange={e => setEmailSubject(e.target.value)} data-testid="email-subject-input" /></div>
                <div>
                  <Label>Message (optional)</Label>
                  <textarea className="w-full mt-1 h-40 rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" value={emailMessage} onChange={e => setEmailMessage(e.target.value)} placeholder="Add a short personal note for the client..." data-testid="email-message-input" />
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <FileSignature size={13} />
                  <span><b>Attachment:</b> {estimateName}_Client_Quote.pdf</span>
                </div>
              </div>
              {/* Preview */}
              <div className="col-span-3 bg-stone-50 border-l p-5 overflow-y-auto">
                <p className="text-xs font-semibold text-stone-600 uppercase tracking-wide mb-2">Email Preview</p>
                <div className="bg-white border border-stone-200 rounded-lg p-5 text-sm shadow-sm">
                  <div className="pb-3 mb-3 border-b text-xs text-stone-500">
                    <div><b>From:</b> Bella Casa Interiors &lt;hello@bellacasa.com&gt;</div>
                    <div><b>To:</b> {emailTo || <span className="text-red-500 italic">(recipient required)</span>}</div>
                    {emailCC && <div><b>CC:</b> {emailCC}</div>}
                    <div><b>Subject:</b> {emailSubject || 'Your Bella Casa Project Estimate'}</div>
                  </div>
                  <div className="text-stone-800 leading-relaxed whitespace-pre-wrap">
                    {emailMessage || `Hello,\n\nThank you for considering Bella Casa Interiors for your ${isKitchen ? 'kitchen' : 'storage'} project.\n\nPlease find attached our detailed estimate — ${estimateName}. This quote includes the module breakdown, material specifications, accessories and a wide-angle AI render of your design concept.\n\nKey highlights:\n• Investment: Rs ${Math.round(finalPricing.finalClient).toLocaleString('en-IN')} (incl. GST)\n• Valid for 30 days from date of issue\n• 1-year workmanship warranty\n\nWe're happy to walk you through every item in a quick 15-min call. Do reply to this email with any questions.\n\nWarm regards,\nTeam Bella Casa Interiors`}
                  </div>
                  <div className="mt-4 pt-3 border-t flex items-center gap-3 text-xs text-stone-500">
                    <div className="w-10 h-10 rounded bg-stone-100 flex items-center justify-center"><FileSignature size={16} className="text-stone-400" /></div>
                    <div>
                      <p className="font-semibold text-stone-700">{estimateName}_Client_Quote.pdf</p>
                      <p>Branded estimate · {renderedImage ? 'AI render included' : 'render will be auto-generated on send'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-white">
              <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(false)} disabled={sendingEmail}>Cancel</Button>
              <Button size="sm" onClick={sendQuoteEmail} disabled={sendingEmail || !emailTo} data-testid="email-quote-send-btn">
                <Mail size={14} className="mr-1" />{sendingEmail ? 'Sending...' : 'Send Quote'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
